/*
 * THE APPLICATION STYLESHEET.
 *
 * Moved here VERBATIM from infostation.html's <style> block at the flip, minus not one rule.
 * That is the whole point: every React region renders the class names this file already
 * defined — `.btn`, `.card`, `.perm-tab`, `.docpill`, `.node` — which is what let every
 * pixel baseline and every mutation-tested spec pass unedited through twenty-three region
 * ports. A visual refresh is a separate piece of work with its own baselines; folding one into
 * the flip would have removed the safety net at the step that needed it most.
 *
 * THE TOKENS AT THE TOP ARE THE DEFINITION. `:root` and `.light` are where every custom
 * property this app uses is set, and `client/src/theme/tokens.ts` mirrors them so MUI can be
 * themed from the same values. The two are held together by a test
 * (`client/src/theme/tokens.test.ts`) that parses THIS FILE and fails on drift — the comment
 * in tokens.ts used to say "the two copies must agree", and now something checks.
 *
 * `.beanrow`'s height is coupled to `ROW_H` in `lib/layout/constants.ts` in the same way and
 * for a sharper reason: `docExtraHeight()` reserves exactly that much per documentation row,
 * and if the two disagree the expanded layout desynchronises silently — nodes overlap, wires
 * miss — with nothing erroring. That pair is checked too.
 */
:root{
    --bg:#0e1116;
    --panel:#161b22;
    --panel-2:#1c232d;
    --line:#2a3441;
    --line-strong:#3a4756;
    --ink:#e7edf5;
    --ink-dim:#9aa7b6;
    --ink-faint:#5f6c7b;
    --accent:#5b8cff;
    --doc-color:#5b8cff;      /* documentation bean — blue, order 1 (matches --accent) */
    --course-color:#3ec97a;   /* course bean — green, order 2 */
    --internal-color:#f2a93b; /* internal doc bean — orange, order 3 */
    --demo-color:#a06bff;     /* demo bean — purple, order 4 (matches root-node gradient) */
    /* documentation workflow status colors (see STATUS_DISPLAY / effectiveDocStatus) */
    --status-not-started:#6b7684;    /* grey */
    --status-prompt-present:#7db8e8;  /* light blue (derived: not_started + a prompt) */
    --status-doc-generated:#5b8cff;   /* blue */
    --status-doc-rejected:#ef4444;    /* red */
    --status-proofread:#a06bff;       /* purple */
    --status-approved:#3ec97a;        /* green */
    --status-needs-update:#f2a93b;    /* amber */
    /* human review lifecycle, per doc page AND locale (see REVIEW_STATES in lib/docs.ts).
       NOT the seven above: those are the AI generation backlog. Theme-independent, so they
       live in :root only — .light is the difference, and there is none. */
    --review-draft:#8b95a3;          /* slate */
    --review-in-review:#f2a93b;      /* amber */
    --review-approved:#3ec97a;       /* green */
    --radius:12px;
    --font-display:'Space Grotesk',system-ui,sans-serif;
    --font:'Inter',system-ui,sans-serif;
  }
  .light{
    --bg:#f4f6fa;--panel:#ffffff;--panel-2:#f0f3f8;
    --line:#dbe1ea;--line-strong:#c2ccd9;
    --ink:#1a212b;--ink-dim:#586475;--ink-faint:#9aa7b6;
  }
  *{box-sizing:border-box;margin:0;padding:0}
  html,body{height:100%}
  body{
    background:var(--bg);color:var(--ink);font-family:var(--font);
    overflow:hidden;-webkit-font-smoothing:antialiased;
  }
  .topbar{
    position:fixed;top:0;left:0;right:0;height:60px;z-index:30;
    display:flex;align-items:center;gap:12px;padding:0 18px;
    background:color-mix(in srgb, var(--panel) 82%, transparent);
    backdrop-filter:blur(14px);border-bottom:1px solid var(--line);
  }
  /* Three flex zones: left (brand + edit tools) and right (view/account tools) size
     to their content; the center (search) takes the space between them and shrinks
     to fit, so the search never overlaps the side buttons when the window is small
     or zoomed in. On narrow widths the media queries below drop button labels to
     reclaim room, and finally hide the search if there still isn't space. */
  /* Side zones keep their natural width (flex:0 0 auto) so their buttons never spill
     out of their own box into the center. Only the center shrinks — the search bar
     gives up width first, down to nothing, so it can never cover a side button. */
  .tb-left{display:flex;align-items:center;gap:14px;flex:0 0 auto}
  .tb-center{flex:1 1 auto;display:flex;align-items:center;justify-content:center;gap:10px;min-width:0}
  .tb-right{display:flex;align-items:center;gap:10px;flex:0 0 auto}
  .brand{display:flex;align-items:center;gap:10px;font-family:var(--font-display);font-weight:600;font-size:16px;letter-spacing:-.01em}
  .brand .mark{width:26px;height:26px;border-radius:8px;background:linear-gradient(135deg,#5b8cff,#a06bff);display:grid;place-items:center;color:#fff;font-weight:600;font-size:13px}
  .brand small{display:block;font-family:var(--font);font-weight:400;font-size:11px;color:var(--ink-dim);letter-spacing:0}
  /* The version line is a real <button>: it opens the About panel, so it has to be
     reachable by keyboard and announced as a control. The dotted underline is a permanent
     (very quiet) affordance — nobody hovers a version number to see whether it does
     something, so a hover-only cue would never be found. */
  .brand .brandver{display:block;font-family:var(--font);font-weight:400;font-size:11px;
    color:var(--ink-dim);letter-spacing:0;background:none;border:none;padding:0;margin:0;
    cursor:pointer;text-align:left;line-height:1.35;
    border-bottom:1px dotted var(--line-strong);transition:color .12s,border-color .12s}
  .brand .brandver:hover{color:var(--ink);border-bottom-color:var(--ink-dim)}
  .brand .brandver:focus-visible{outline:2px solid var(--accent);outline-offset:3px;border-radius:3px}

  /* About panel — same floating-card idiom as .linkmenu, sized for prose. */
  #about{position:fixed;z-index:50;top:56px;left:14px;width:360px;max-width:calc(100vw - 28px);
    max-height:calc(100vh - 76px);overflow:auto;display:none;
    background:var(--panel);border:1px solid var(--line);border-radius:12px;
    box-shadow:0 12px 34px rgba(0,0,0,.4);padding:14px 15px;font-size:13px;color:var(--ink)}
  #about.show{display:block}
  #about .ab-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px;margin-bottom:10px}
  #about .ab-name{font-family:var(--font-display);font-weight:600;font-size:14px}
  #about .ab-ver{font-size:12.5px;color:var(--ink-dim)}
  #about .ab-deploy{display:flex;align-items:center;gap:7px;font-size:12.5px;margin-bottom:9px}
  #about .ab-dot{width:8px;height:8px;border-radius:50%;background:var(--dc,var(--ink-faint));flex:0 0 auto}
  #about .ab-row{display:flex;align-items:center;justify-content:space-between;gap:10px;
    font-size:12.5px;color:var(--ink-dim);padding:2px 0}
  #about .ab-row.off{color:var(--ink-faint)}
  #about .ab-mark{font-size:12px;color:var(--accent)}
  #about .ab-row.off .ab-mark{color:var(--ink-faint)}
  #about .ab-sep{height:1px;background:var(--line);margin:12px 0}
  #about .ab-h{font-size:11px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;
    color:var(--ink-faint);margin-bottom:9px}
  #about .ab-rel{margin-bottom:13px}
  #about .ab-rel:last-child{margin-bottom:2px}
  #about .ab-relhead{display:flex;align-items:baseline;gap:8px;margin-bottom:3px}
  #about .ab-relver{font-weight:600;font-size:12.5px}
  #about .ab-reldate{font-size:11.5px;color:var(--ink-faint)}
  #about .ab-relhead .ab-now{font-size:10.5px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;
    color:var(--accent);border:1px solid color-mix(in srgb,var(--accent) 45%,transparent);
    border-radius:5px;padding:1px 5px}
  #about .ab-line{font-size:12.5px;color:var(--ink-dim);line-height:1.5;margin-bottom:5px}
  #about ul.ab-items{margin:0;padding-left:16px}
  #about ul.ab-items li{font-size:12.5px;line-height:1.5;color:var(--ink-dim);margin:3px 0}
  #about .ab-note{font-size:11.5px;color:var(--ink-faint);line-height:1.5;margin-top:11px}
  /* Older releases collapse, so the panel opens at a readable height instead of filling the
     screen. Same disclosure marker as the node popup's AI panel, so it reads as one app. */
  #about details.ab-rel>summary{list-style:none;cursor:pointer;display:flex;align-items:baseline;
    gap:8px;padding:2px 0;border-radius:6px}
  #about details.ab-rel>summary::-webkit-details-marker{display:none}
  #about details.ab-rel>summary::before{content:'▸';font-size:10px;color:var(--ink-faint);
    transition:transform .12s ease;align-self:center}
  #about details.ab-rel[open]>summary::before{transform:rotate(90deg)}
  #about details.ab-rel>summary:hover .ab-relver{color:var(--accent)}
  #about details.ab-rel .ab-line{margin:4px 0 0 18px}
  #about details.ab-rel ul.ab-items{margin-left:18px}
  .segmented{display:flex;background:var(--panel-2);border:1px solid var(--line);border-radius:10px;padding:3px;gap:2px}
  .seg{
    border:none;background:transparent;color:var(--ink-dim);font-family:var(--font);
    font-size:13px;font-weight:500;padding:7px 13px;border-radius:7px;cursor:pointer;
    display:flex;align-items:center;gap:6px;transition:.15s;white-space:nowrap;
  }
  .seg:hover{color:var(--ink)}
  .seg.on{background:var(--panel);color:var(--ink);box-shadow:0 1px 3px rgba(0,0,0,.18)}
  .seg.on .ico{color:var(--accent)}
  /* .seg-slide (V0.37.1): the Users/Groups toggle in the responsibles picker below. A
     modifier on .segmented/.seg above — neither of those rules is touched. The thumb is one
     absolutely-positioned span at width:50%, so translateX(100%) for the second state
     resolves against the track's own padding box: exact halves, no measurement, no JS
     layout. .hue windows a double-width gradient so the colour shifts continuously across
     the travel (accent blue -> the root-node purple) instead of cross-fading at the ends. */
  .seg-slide{position:relative;gap:0}
  .seg-slide .seg{position:relative;z-index:1;flex:1 1 0;justify-content:center}
  .seg-slide .seg.on{background:transparent;box-shadow:none;color:#fff}
  .seg-thumb{position:absolute;left:0;top:0;bottom:0;width:50%;border-radius:7px;z-index:0;
    background:var(--accent);box-shadow:0 1px 3px rgba(0,0,0,.18);
    transition:transform .18s cubic-bezier(.4,0,.2,1),background-position .18s cubic-bezier(.4,0,.2,1)}
  .seg-slide[data-mode="groups"] .seg-thumb{transform:translateX(100%)}
  .seg-slide.hue .seg-thumb{background:linear-gradient(90deg,var(--accent) 0%,var(--demo-color) 100%);
    background-size:200% 100%;background-position:0% 0}
  .seg-slide.hue[data-mode="groups"] .seg-thumb{background-position:100% 0}
  @media(prefers-reduced-motion:reduce){.seg-thumb{transition:none}}

  /* The subject picker (V0.37.1) — chips, search and the slider above, replacing the plain
     checkbox list the responsibles picker used through V0.37. Shares .searchbox/.search-wrap
     (the topbar's own search look) for the input box rather than inventing a second one. */
  .subj-picker{position:relative}
  .subj-box{background:var(--panel-2);border:1px solid var(--line);border-radius:10px;padding:7px;
    display:flex;flex-wrap:wrap;gap:6px;align-items:center;cursor:text;transition:.15s}
  .subj-box:focus-within{border-color:var(--accent)}
  .subj-tok{display:inline-flex;align-items:center;gap:7px;background:var(--panel);border:1px solid var(--line);
    border-radius:20px;padding:4px 5px;font-size:13px;color:var(--ink);max-width:100%}
  .subj-tok.grp{background:color-mix(in srgb,var(--demo-color) 12%,transparent);
    border-color:color-mix(in srgb,var(--demo-color) 40%,transparent)}
  .subj-tok .x{border:none;background:none;color:var(--ink-faint);cursor:pointer;font-size:14px;
    line-height:1;padding:2px 5px;border-radius:50%}
  .subj-tok .x:hover{color:#ff7a7a}
  .subj-input{flex:1 1 90px;min-width:90px;border:none;background:none;color:var(--ink);font:inherit;
    font-size:13.5px;outline:none;padding:5px 4px}
  .subj-input::placeholder{color:var(--ink-faint)}
  /* The dropdown: absolute, so opening it OVERLAYS what follows instead of pushing the rest
     of the popup (or the doc bar's popover) down the moment somebody clicks the box. */
  .subj-menu{position:absolute;left:0;right:0;top:calc(100% + 6px);z-index:20;
    background:var(--panel);border:1px solid var(--line);
    border-radius:12px;box-shadow:0 12px 34px rgba(0,0,0,.4);padding:9px}
  .subj-list{margin-top:9px;max-height:200px;overflow-y:auto;display:flex;flex-direction:column;gap:1px}
  .subj-row{display:flex;align-items:center;gap:9px;padding:7px 8px;border-radius:8px;cursor:pointer;
    background:none;border:none;width:100%;text-align:left;font:inherit;color:var(--ink);transition:.12s}
  .subj-row:hover{background:var(--panel-2)}
  .subj-row.sel{background:color-mix(in srgb,var(--accent) 13%,transparent)}
  .subj-row:disabled{cursor:default;opacity:.55}
  .subj-row:disabled:hover{background:none}
  .subj-av{width:24px;height:24px;border-radius:50%;flex:0 0 auto;display:grid;place-items:center;
    font-size:10px;font-weight:600;color:#fff;letter-spacing:.02em;background:var(--accent)}
  .subj-av.grp{border-radius:7px;background:color-mix(in srgb,var(--demo-color) 22%,transparent);
    color:var(--demo-color);border:1px solid color-mix(in srgb,var(--demo-color) 45%,transparent)}
  .subj-who{flex:1;min-width:0}
  .subj-nm{display:block;font-size:13.5px;line-height:1.35;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .subj-sub{display:block;font-size:11.5px;color:var(--ink-faint);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .subj-count{font-size:11.5px;color:var(--ink-dim);background:var(--panel-2);border:1px solid var(--line);
    border-radius:20px;padding:2px 9px;flex:0 0 auto;font-variant-numeric:tabular-nums}
  .subj-tick{width:14px;text-align:center;font-size:12px;color:var(--accent);flex:0 0 auto}
  .subj-empty{font-size:12.5px;color:var(--ink-faint);font-style:italic;padding:16px 10px;text-align:center}
  .btn{
    border:1px solid var(--line);background:var(--panel);color:var(--ink-dim);
    font-family:var(--font);font-size:13px;font-weight:500;padding:8px 12px;border-radius:9px;cursor:pointer;
    display:flex;align-items:center;gap:6px;transition:.15s;
  }
  .btn.on{background:var(--accent);border-color:var(--accent);color:#fff}
  .btn.on .ico{color:#fff}
  .btn:hover{color:var(--ink);border-color:var(--line-strong)}
  .btn.danger:hover{color:#ff7a7a;border-color:#ff7a7a55}
  .editswitch{font-weight:600;width:140px;flex:0 0 auto;justify-content:center}
  .editswitch .dot-ind{width:9px;height:9px;border-radius:50%;background:var(--ink-faint);transition:.15s;flex:0 0 auto}
  /* .btn.on now fills the whole button solid accent-blue (added for the Link/Hide-links
     toggles) — text/dot/icon here must go white to stay visible against that fill,
     not the same accent blue they used when the button background stayed neutral. */
  .editswitch.on{color:#fff}
  .editswitch.on .dot-ind{background:#fff;box-shadow:0 0 0 4px rgba(255,255,255,.3)}
  .ico{width:16px;height:16px;stroke:currentColor;stroke-width:2;fill:none;stroke-linecap:round;stroke-linejoin:round;flex:0 0 auto}
  #stage{position:fixed;inset:60px 0 0 0;overflow:hidden;cursor:grab;touch-action:none}
  #stage.grabbing{cursor:grabbing}
  #stage.addmode{cursor:copy}
  #stage.linkmode{cursor:crosshair}
  /* The map region's host (client/src/features/map/MapStage.tsx). It renders BOTH the wire
     <svg> and the world <div> into here — two React roots would have to apply the camera
     twice and could not share one pointer handler — so this fills #stage exactly, and the
     two keep the containing block they have always had. */
  #maproot{position:absolute;inset:0}
  /* The OS-default grab/grabbing cursor renders near-white on Windows — invisible against
     the light theme's pale background. Swap in a dark-filled hand cursor for light mode only. */
  body.light #stage,body.light .node{cursor:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E%3Crect x='6' y='8' width='9' height='10' rx='3' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3Crect x='4' y='6' width='3' height='7' rx='1.5' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3Crect x='7' y='3' width='3' height='9' rx='1.5' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3Crect x='10' y='3' width='3' height='9' rx='1.5' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3Crect x='13' y='4' width='3' height='8' rx='1.5' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3C/svg%3E") 10 10,grab}
  body.light #stage.grabbing,body.light .node.dragging{cursor:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E%3Crect x='6' y='9' width='9' height='9' rx='3' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3Crect x='4' y='8' width='3' height='4' rx='1.5' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3Crect x='7' y='6' width='3' height='5' rx='1.5' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3Crect x='10' y='6' width='3' height='5' rx='1.5' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3Crect x='13' y='7' width='3' height='5' rx='1.5' fill='%231a1a1a' stroke='white' stroke-width='0.5'/%3E%3C/svg%3E") 10 10,grabbing}
  svg#wires{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible}
  #wires .xlink{pointer-events:none}
  #wires .xlink-hit{pointer-events:stroke;cursor:pointer}
  #wires .xlink-hit:hover + .xlink{stroke-width:3.6}
  .linkmenu{position:fixed;z-index:50;background:var(--panel);border:1px solid var(--line);border-radius:12px;
    box-shadow:0 12px 34px rgba(0,0,0,.4);padding:12px 13px;min-width:190px;font-size:13px;color:var(--ink)}
  .linkmenu .lm-title{display:block;font-family:var(--font-display);font-weight:600;font-size:13px;margin-bottom:8px}
  .linkmenu .lm-row{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;color:var(--ink-dim)}
  .linkmenu .lm-row input[type=color]{-webkit-appearance:none;appearance:none;width:34px;height:22px;padding:0;
    border:1px solid var(--line);border-radius:6px;background:transparent;cursor:pointer}
  .linkmenu .lm-row input[type=color]::-webkit-color-swatch-wrapper{padding:0}
  .linkmenu .lm-row input[type=color]::-webkit-color-swatch{border:none;border-radius:5px}
  .linkmenu .lm-btns{display:flex;gap:8px}
  .linkmenu button{flex:1;border:1px solid var(--line);background:var(--panel);color:var(--ink);font:inherit;
    font-size:12.5px;padding:7px 8px;border-radius:8px;cursor:pointer}
  .linkmenu button:hover{border-color:var(--line-strong)}
  .linkmenu .lm-del:hover{color:#ff7a7a;border-color:#ff7a7a55}
  /* THE RIGHT-CLICK MENUS (V0.54, features/contextmenu/ContextMenu.tsx) — the .linkmenu floating
     card above, grown into a list: the node it was opened on as a chip, then one row per item —
     icon, label, the gesture that does the same thing — with a hairline between groups. Fixed and
     top level (#ctxmenu), above the popup and the document view, so it can open over either.
     The swatches are the branch colours (lib/color.ts BRANCH_COLORS). */
  #ctxmenu .ctxmenu{position:fixed;z-index:90;min-width:220px;max-width:360px;max-height:calc(100vh - 16px);
    overflow:auto;background:var(--panel);border:1px solid var(--line);border-radius:12px;
    box-shadow:0 12px 34px rgba(0,0,0,.4);padding:5px;font-size:13px;color:var(--ink);outline:none}
  .ctxmenu .cm-head{display:flex;padding:3px 3px 7px;margin:0 0 4px;border-bottom:1px solid var(--line)}
  .ctxmenu .cm-head .aidoc-node{font-size:12.5px;padding:5px 11px}
  .ctxmenu .cm-title{font-family:var(--font-display);font-weight:600;font-size:13px;padding:4px 6px 1px}
  .ctxmenu .cm-item{display:flex;align-items:center;gap:9px;width:100%;height:30px;padding:0 9px;border:none;
    background:none;border-radius:7px;color:var(--ink);font:inherit;font-size:13px;text-align:left;cursor:pointer}
  .ctxmenu .cm-item:hover,.ctxmenu .cm-item:focus-visible,.ctxmenu .cm-item.open{background:var(--panel-2);outline:none}
  .ctxmenu .cm-item .ico{width:15px;height:15px;color:var(--ink-dim)}
  .ctxmenu .cm-item:hover .ico,.ctxmenu .cm-item:focus-visible .ico{color:var(--ink)}
  .ctxmenu .cm-dot{width:9px;height:9px;border-radius:50%;flex:0 0 auto;margin:0 3px}
  .ctxmenu .cm-lbl{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .ctxmenu .cm-keys{font-size:11px;color:var(--ink-faint);white-space:nowrap}
  /* #i-chev points down; a submenu opens to the side, so its arrow does too. */
  .ctxmenu .cm-item .ico.cm-trail{width:13px;height:13px;color:var(--ink-faint);transform:rotate(-90deg)}
  .ctxmenu .cm-item .ico.cm-check{color:var(--accent);stroke-width:2.4}
  .ctxmenu .cm-item.danger,.ctxmenu .cm-item.danger .ico{color:#ff7a7a}
  body.light .ctxmenu .cm-item.danger,body.light .ctxmenu .cm-item.danger .ico{color:#d9434a}
  .ctxmenu .cm-item.danger:hover,.ctxmenu .cm-item.danger:focus-visible{background:color-mix(in srgb,#ff7a7a 12%,transparent)}
  .ctxmenu .cm-sep{height:1px;background:var(--line);margin:4px 6px}
  .ctxmenu .cm-swatches{display:grid;grid-template-columns:repeat(5,26px);gap:6px;padding:6px 7px}
  .ctxmenu .cm-sw{width:26px;height:26px;border-radius:8px;border:2px solid transparent;padding:0;cursor:pointer}
  .ctxmenu .cm-sw.on{border-color:var(--ink);box-shadow:inset 0 0 0 2px var(--panel)}
  .ctxmenu .cm-sw:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
  /* A "Move to…" is waiting for its new parent: the Link tool's cursor, for the same kind of pick. */
  #stage.movemode,#stage.movemode .node{cursor:crosshair}
  #world{position:absolute;left:0;top:0;transform-origin:0 0}
  .node{
    position:absolute;box-sizing:border-box;min-height:42px;width:max-content;max-width:230px;
    padding:9px 13px;border-radius:11px;display:flex;align-items:center;gap:9px;
    font-size:13.5px;font-weight:500;line-height:1.35;cursor:grab;user-select:none;
    border:1px solid;background:var(--panel);color:var(--ink);
    box-shadow:0 2px 10px rgba(0,0,0,.14);transition:box-shadow .15s,transform .06s;
  }
  .node:hover{box-shadow:0 4px 18px rgba(0,0,0,.24)}
  .node.dragging{cursor:grabbing;box-shadow:0 10px 28px rgba(0,0,0,.35);z-index:5}
  /* The node is elsewhere (V0.47) — in a popup's seat, a page's bubble, or crossing between
     them. Only its paint goes: the box keeps its layout so nothing around it moves, and the
     wires still have an anchor. Both node-shaped boxes share the rule, so the same fact hides
     the same node on either surface. */
  .node.away,.docnav-node.away{opacity:0;pointer-events:none}
  .node.drop-target{outline:2.5px dashed var(--accent);outline-offset:3px;
    box-shadow:0 0 0 4px color-mix(in srgb,var(--accent) 22%,transparent),0 6px 20px rgba(0,0,0,.3)}
  .node.root{font-family:var(--font-display);font-weight:600;font-size:15px;padding:12px 18px;color:#fff;border:none}
  .node.linksel{outline:2px solid var(--accent);outline-offset:2px}
  .node .swatch{width:9px;height:9px;border-radius:50%;flex:0 0 auto}
  .node .txt{white-space:normal;word-break:break-word;overflow-wrap:anywhere}
  .node .toggle{position:absolute;top:50%;width:20px;height:20px;border-radius:50%;
    border:1.5px solid var(--line-strong);background:var(--panel);color:var(--ink-dim);
    font-size:14px;line-height:1;display:grid;place-items:center;cursor:pointer;
    transform:translateY(-50%);padding:0;font-weight:600;z-index:3;transition:.12s}
  .node .toggle:hover{background:var(--panel-2)}
  .node .toggle{right:-10px}
  .node .toggle.left{right:auto;left:-10px}
  .node .edit{background:transparent;border:none;outline:none;color:inherit;font:inherit;width:100%;min-width:60px}
  .node .close{margin-left:2px;width:16px;height:16px;opacity:0;stroke:currentColor;stroke-width:2.2;fill:none;cursor:pointer;flex:0 0 auto;transition:opacity .12s}
  .node:hover .close{opacity:.5}
  .node .close:hover{opacity:1}
  body.viewonly .node{cursor:pointer}
  body.viewonly .node .close{display:none}
  /* The clearance-heatmap state badge (V0.42): a bucket-coloured chip on the node's top-left
     corner — clear of the collapse toggle (mid-side), the delete cross (top-right) and the
     presence avatars (bottom). Colour is inline from HEAT_HEX; the glyph is near-black so it
     reads on every bright bucket. Slate carries no icon, so it draws no badge. */
  .node .heat-badge{position:absolute;top:-9px;left:-9px;width:19px;height:19px;border-radius:50%;
    background:currentColor;border:2px solid var(--panel);padding:2.5px;z-index:6;pointer-events:none;
    box-shadow:0 1px 3px rgba(0,0,0,.4)}
  .node .heat-badge use{fill:none;stroke:#10151c;stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round}
  .hint{position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:20;
    background:color-mix(in srgb,var(--panel) 90%,transparent);border:1px solid var(--line);
    backdrop-filter:blur(10px);padding:8px 15px;border-radius:20px;font-size:12.5px;color:var(--ink-dim);
    display:flex;align-items:center;gap:7px;box-shadow:0 4px 14px rgba(0,0,0,.16);
    transition:opacity .25s ease}
  .hint b{color:var(--ink);font-weight:500}
  /* The hint steps aside (V0.53.4): `.gone` is set by chrome.ts's dismissHint() at the first
     movement of a pan and cleared by updateHint() when the wording changes (a view <-> edit
     switch, the Link tool). Opacity rather than display, so the line fades instead of
     snapping and keeps its place for its return; pointer-events off so it never sits over a
     node the reader is trying to reach. */
  .hint.gone{opacity:0;pointer-events:none}
  @media (prefers-reduced-motion: reduce){.hint{transition:none}}
  .zoom{position:fixed;bottom:16px;right:16px;z-index:20;display:flex;flex-direction:column;
    background:var(--panel);border:1px solid var(--line);border-radius:11px;overflow:hidden;box-shadow:0 4px 14px rgba(0,0,0,.16)}
  .zoom button{border:none;background:transparent;color:var(--ink-dim);width:38px;height:36px;cursor:pointer;display:grid;place-items:center}
  .zoom button:hover{background:var(--panel-2);color:var(--ink)}
  .zoom button+button{border-top:1px solid var(--line)}
  /* The minimap's toggle (V0.53.4) is the stack's first row and lights up while it is on, the
     same accent the documents toggle uses for its pressed state. */
  .zoom button.on{color:var(--accent)}
  /* The documents toggle (V0.42.2): pin the documentation rows open at any zoom. Its OWN card
     above the zoom stack — `.zoom` is a single bordered card whose buttons are divided by an
     internal hairline, so a control that must read as SEPARATE needs its own edges rather than
     a row of the stack. The offset is the stack's height: 16 bottom + 4x36 buttons + 3
     hairlines = 163 (the minimap's row, V0.53.4, made it four), plus an 8px gap. Same card
     treatment as .zoom (not .heatlegend's blurred family). */
  .docstoggle{position:fixed;bottom:171px;right:16px;z-index:20;
    background:var(--panel);border:1px solid var(--line);border-radius:11px;overflow:hidden;box-shadow:0 4px 14px rgba(0,0,0,.16)}
  .docstoggle button{border:none;background:transparent;color:var(--ink-dim);width:38px;height:36px;cursor:pointer;display:grid;place-items:center}
  .docstoggle button:hover{background:var(--panel-2);color:var(--ink)}
  .docstoggle button.on{color:var(--accent)}
  /* ---- the minimap (V0.53.4; src/features/minimap/Minimap.tsx) ----
     A card at the top right of the stage, 10px in from the top bar and the edge, in the
     .zoom/.docstoggle card family. Its SIZE is inline — the component derives it from the
     map's shape (lib/minimap.ts) — and so is the fog's colour, which is per theme and not a
     token. The card RESTS at a third of its opacity (.26 = .75 x .35) and is bright for 1.8s
     after every camera write — it is information about a MOVE, and clutter the rest of the
     time. THE 1.8s IS THE TRANSITION-DELAY BELOW, NOT A TIMER: the component puts `.active`
     on and takes it off two frames later (after a style pass has seen it), which snaps the card
     to .75 (`.active` has no transition) and, on removal, re-arms the delayed fade back to .26.
     Hovering brightens at
     once (no delay) and grows the card from its top-right corner, where it is anchored;
     `.dragging` is the frame being pulled. The point of doing it in CSS: Playwright's
     `animations:'disabled'` fast-forwards a pending transition, so the pixel baselines always
     photograph the resting card, whatever the machine's timing. Under prefers-reduced-motion
     the fade is a step (delay kept, duration zero) and the growth is off. z 21: above the
     stage and the zoom stack, below the top bar's menus. */
  #minimap .minimap{position:fixed;top:70px;right:10px;z-index:21;
    background:var(--panel);border:1px solid var(--line);border-radius:11px;overflow:hidden;
    box-shadow:0 4px 14px rgba(0,0,0,.16);opacity:.26;transform-origin:100% 0;
    transition:opacity .25s ease 1.8s,transform .18s ease}
  #minimap .minimap.active,#minimap .minimap:hover{opacity:.75}
  /* "Keep it bright" (V0.54, the card's right-click menu): the waking level, at rest too. */
  #minimap .minimap.bright{opacity:.75}
  #minimap .minimap.active{transition:none}
  #minimap .minimap:hover{transition-delay:0s}
  /* Grown while hovered, and held grown while the frame is being pulled: the drag's pointer
     maths was taken at pointerdown through the card's own matrix, and a card that shrank as
     the pointer left it mid-drag would make the frame leap. */
  #minimap .minimap:hover,#minimap .minimap.dragging{transform:scale(1.5)}
  /* Two layers: the PICTURE (sheet, wires, node marks) is a canvas — hundreds of marks redrawn
     in a few milliseconds with no DOM to reconcile — and the frame, its fog and its hit area
     are an SVG over it, so they stay crisp under the growth and keep the viewBox the pointer
     maths reads. */
  #minimap .mm-canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
  #minimap .minimap svg{position:absolute;inset:0;display:block;width:100%;height:100%;cursor:crosshair}
  #minimap .mm-fog{pointer-events:none}
  #minimap .mm-frame{fill:var(--accent);fill-opacity:.12;stroke:var(--accent);stroke-width:1.5;
    vector-effect:non-scaling-stroke;pointer-events:none}
  #minimap .mm-hit{fill:transparent;pointer-events:all;cursor:grab}
  #minimap .minimap.dragging .mm-hit{cursor:grabbing}
  @media (prefers-reduced-motion: reduce){
    #minimap .minimap{transition:opacity 0s linear 1.8s}
    #minimap .minimap:hover,#minimap .minimap.dragging{transform:none}
  }
  /* AI-doc static toolbar over the map (top-left, below the 60px topbar) */
  #aidoc-toolbar{position:fixed;top:66px;left:64px;z-index:22;display:none;align-items:center;gap:10px;
    background:color-mix(in srgb,var(--panel) 92%,transparent);backdrop-filter:blur(10px);
    border:1px solid var(--line);border-radius:12px;padding:8px 10px;box-shadow:0 6px 20px rgba(0,0,0,.22);
    transition:left .22s ease}
  #aidoc-toolbar.show{display:flex}
  #aidoc-toolbar .aidoc-title{font-family:var(--font-display);font-weight:600;font-size:12.5px;color:var(--accent);padding:0 2px}
  #aidoc-toolbar .status-field{min-width:190px}
  @media(max-width:720px){#aidoc-toolbar .status-field{min-width:130px}#aidoc-toolbar #btn-generate-docs span{display:none}}
  /* The clearance heatmap's legend (V0.39): bottom-left of the map, above the hint line.
     It follows the sidebar through --stage-left, which applySidebar() keeps in step; the
     bucket colours themselves are inline (HEAT_HEX in lib/health.ts — the one definition). */
  #heatmap-legend{display:none}
  #heatmap-legend.show{display:block}
  .heatlegend{position:fixed;bottom:56px;left:calc(var(--stage-left,0px) + 16px);z-index:20;
    display:flex;align-items:center;gap:12px;padding:8px 12px;
    background:color-mix(in srgb,var(--panel) 92%,transparent);backdrop-filter:blur(10px);
    border:1px solid var(--line);border-radius:12px;box-shadow:0 6px 20px rgba(0,0,0,.22);
    font-size:12px;color:var(--ink-dim);transition:left .22s ease}
  .heatlegend-title{font-family:var(--font-display);font-weight:600;font-size:12.5px;color:var(--accent);padding:0 2px}
  .heatlegend-row{display:inline-flex;align-items:center;gap:6px}
  .heatlegend-dot{width:16px;height:16px;border-radius:50%;flex:none;display:grid;place-items:center}
  .heatlegend-dot svg{width:11px;height:11px}
  .heatlegend-dot use{fill:none;stroke:#10151c;stroke-width:2.4;stroke-linecap:round;stroke-linejoin:round}
  .heatlegend-count{color:var(--ink);font-weight:600;font-variant-numeric:tabular-nums}
  .saved{position:fixed;top:70px;left:50%;transform:translateX(-50%) translateY(-8px);z-index:40;
    background:var(--accent);color:#fff;font-size:12.5px;font-weight:500;padding:6px 14px;border-radius:18px;
    opacity:0;pointer-events:none;transition:.3s}
  .saved.show{opacity:1;transform:translateX(-50%) translateY(0)}

  /* ---- sidebar ---- */
  #sidebar{position:fixed;top:60px;bottom:0;left:0;width:260px;z-index:25;
    background:var(--panel);border-right:1px solid var(--line);
    display:flex;flex-direction:column;transition:transform .22s ease}
  #sidebar.closed{transform:translateX(-260px)}
  #sidebar-resize{position:absolute;top:0;right:0;width:6px;height:100%;cursor:col-resize;z-index:28;touch-action:none}
  #sidebar-resize:hover,body.resizing-sidebar #sidebar-resize{background:color-mix(in srgb,var(--accent) 38%,transparent)}
  body.resizing-sidebar{cursor:col-resize;user-select:none}
  body.resizing-sidebar #sidebar,body.resizing-sidebar .side-toggle,body.resizing-sidebar #stage,body.resizing-sidebar #aidoc-toolbar{transition:none}
  .side-head{display:flex;align-items:center;gap:8px;padding:14px 14px 10px;border-bottom:1px solid var(--line)}
  .side-head h2{font-family:var(--font-display);font-size:14px;font-weight:600;flex:1}
  .side-body{flex:1;overflow:auto;padding:8px 8px 20px}
  .side-foot{padding:10px 12px;border-top:1px solid var(--line);display:flex;gap:8px}
  .side-foot input{flex:1;background:var(--panel-2);border:1px solid var(--line);color:var(--ink);
    border-radius:8px;padding:7px 9px;font:inherit;font-size:13px;outline:none}
  .side-foot input:focus{border-color:var(--accent)}
  .icobtn{border:1px solid var(--line);background:var(--panel);color:var(--ink-dim);width:32px;height:32px;
    border-radius:8px;cursor:pointer;display:grid;place-items:center;flex:0 0 auto}
  .icobtn:hover{color:var(--ink);border-color:var(--line-strong)}
  .tagrow{display:flex;align-items:center;gap:7px;padding:5px 7px;border-radius:8px;cursor:pointer;
    font-size:13px;user-select:none;position:relative}
  .tagrow:hover{background:var(--panel-2)}
  .tagrow.active{background:color-mix(in srgb,var(--accent) 18%,transparent)}
  .tagrow .caret{width:14px;flex:0 0 auto;color:var(--ink-faint);cursor:pointer;text-align:center;font-size:10px}
  .tagrow .dot{width:11px;height:11px;border-radius:50%;flex:0 0 auto;border:1px solid rgba(0,0,0,.15);cursor:pointer}
  .tagrow .name{flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .tagrow .count{font-size:11px;color:var(--ink-faint);background:var(--panel-2);padding:1px 6px;border-radius:10px}
  .tagrow .row-x{opacity:0;color:var(--ink-faint);cursor:pointer;font-size:15px;line-height:1;padding:0 2px}
  .tagrow:hover .row-x{opacity:.6}
  .tagrow .row-x:hover{opacity:1;color:#ff7a7a}
  .tagrow[draggable=true]{cursor:grab}
  .tagrow.dragging{opacity:.4}
  .tagrow.drop-into{outline:2px solid var(--accent);outline-offset:-2px;background:color-mix(in srgb,var(--accent) 16%,transparent)}
  .tagrow.drop-before{box-shadow:inset 0 3px 0 var(--accent)}
  .tagrow.drop-after{box-shadow:inset 0 -3px 0 var(--accent)}
  #tag-tree.drop-root{outline:2px dashed var(--line-strong);outline-offset:-4px;border-radius:8px}
  .tagrow input.dot{-webkit-appearance:none;-moz-appearance:none;appearance:none;padding:0;width:13px;height:13px;
    border-radius:50%;border:1px solid rgba(0,0,0,.15);background:transparent;cursor:pointer;flex:0 0 auto}
  .tagrow input.dot::-webkit-color-swatch-wrapper{padding:0}
  .tagrow input.dot::-webkit-color-swatch{border:none;border-radius:50%}
  .tagrow input.dot::-moz-color-swatch{border:none;border-radius:50%}
  .side-toggle{position:fixed;top:70px;left:270px;z-index:26;transition:left .22s ease}
  .side-toggle.closed{left:12px}

  /* ---- node popup ---- */
  #scrim{position:fixed;inset:0;z-index:45;background:rgba(0,0,0,.42);opacity:0;pointer-events:none;transition:.16s}
  #scrim.show{opacity:1;pointer-events:auto}
  #popup{position:fixed;z-index:46;width:560px;max-width:calc(100vw - 32px);
    background:var(--panel);border:1px solid var(--line);border-radius:18px;
    box-shadow:0 20px 60px rgba(0,0,0,.5);padding:26px 28px;display:none;
    max-height:calc(100vh - 80px);overflow:auto}
  /* THE EDITING SHAPE IS AS WIDE AS A DOCUMENT PAGE (V0.49.2); the reading shape is not.
     Editing a node is form work — a name, a description, four documentation slots, the tag
     chips and the AI panel — and at 560px those wrap into a column far narrower than anything
     else the app asks somebody to fill in. `--pagew` is the ONE width every centred reading
     surface already uses (the document column, every admin screen), so this borrows it rather
     than inventing a second number that would drift from it.
     VIEW MODE IS UNTOUCHED, deliberately and checkably: `node-popup.png` photographs a single
     click, which is the read-only shape, and it must not move. The class is written by
     NodePopup's open effect — `host.classList.toggle('readonly', open.ro)`. */
  #popup:not(.readonly){width:var(--pagew);max-width:calc(100vw - 32px)}
  #popup.show{display:block}
  /* The flight host (V0.47): zero-size and fixed at the origin so its children position in
     viewport pixels; above #docmodal (74) because a node crosses INTO the document view, below
     #logdetail and #loginmodal (80) which must never be flown over. */
  #flight{position:fixed;left:0;top:0;width:0;height:0;z-index:75;pointer-events:none}
  /* The clone. Painted by nodeTint like every other node-shaped box, sized per frame by
     src/app/flight.ts — so NO transitions here: a transition on a per-frame write is a fight. */
  .flight-node{position:absolute;box-sizing:border-box;display:inline-flex;align-items:center;
    border:1px solid;white-space:nowrap;font-family:var(--font);font-weight:500;line-height:1.35;
    color:var(--ink);pointer-events:none;box-shadow:0 14px 34px rgba(0,0,0,.45);will-change:left,top,width,height}
  .flight-node.root{font-family:var(--font-display);font-weight:600;color:#fff;border-color:transparent}
  .flight-node .swatch{border-radius:50%;flex:0 0 auto}
  .flight-node .txt{overflow:hidden;text-overflow:ellipsis}
  #popup h3{font-family:var(--font-display);font-size:20px;font-weight:600;margin-bottom:2px;display:flex;align-items:center;gap:10px}
  #popup.readonly h3{font-size:22px;margin-bottom:4px}
  #popup .accentbar{width:12px;height:12px;border-radius:50%;flex:0 0 auto}
  #popup label{display:block;font-size:12px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;
    color:var(--ink-faint);margin:18px 0 8px}
  /* read-only view: plain text, no editable-looking boxes */
  /* .pp-ownertext is .pp-readtext's twin and shares its whole look. It has its OWN class
     because `.pp-readtext` means "the description, as plain text" to a spec that counts
     exactly one of them (e2e/36-popup.spec.ts) — the owner row must not become a second. */
  #popup .pp-readtext,#popup .pp-ownertext{font-size:15px;line-height:1.6;color:var(--ink);white-space:pre-wrap;word-break:break-word}
  #popup .pp-readtext.pp-empty,#popup .pp-ownertext.pp-empty{color:var(--ink-faint);font-style:italic}
  #popup.readonly label{margin-top:20px}
  #popup textarea{width:100%;background:var(--panel-2);border:1px solid var(--line);color:var(--ink);
    border-radius:12px;padding:13px 15px;font:inherit;font-size:14.5px;line-height:1.5;resize:vertical;min-height:150px;outline:none}
  #popup textarea:focus{border-color:var(--accent)}
  #popup textarea:disabled,#popup input:disabled{opacity:.85;cursor:default}
  /* AI documentation panel — one collapsible group for every AI-admin-only field, so
     the popup doesn't sprawl. Only rendered while AI-doc mode is on. */
  #popup .aipanel{margin:18px 0 0;border:1px solid var(--line);border-radius:12px;background:var(--panel-2)}
  #popup .aipanel>summary{list-style:none;cursor:pointer;padding:11px 14px;font-size:12px;font-weight:600;
    letter-spacing:.03em;text-transform:uppercase;color:var(--ink-faint);display:flex;align-items:center;gap:8px}
  #popup .aipanel>summary::-webkit-details-marker{display:none}
  #popup .aipanel>summary::before{content:'▸';font-size:11px;transition:transform .12s ease}
  #popup .aipanel[open]>summary::before{transform:rotate(90deg)}
  #popup .aipanel>summary:hover{color:var(--ink)}
  #popup .aipanel-body{padding:0 14px 14px}
  /* A fold inside a fold (V0.51): `--panel` against the `--panel-2` it sits in, and a smaller
     heading, so two levels read as two levels rather than as two siblings. */
  #popup .aipanel .aipanel.sub{margin:10px 0 0;background:var(--panel)}
  #popup .aipanel .aipanel.sub>summary{padding:8px 12px;font-size:11px}
  #popup .aipanel .aipanel.sub .aipanel-body{padding:0 12px 12px}
  #popup .aipanel-body>label:first-child{margin-top:4px}
  /* A fold's first block sits against its summary, whatever it is (V0.51). */
  #popup .aipanel-body>*:first-child:not(label){margin-top:0}
  #popup .aipanel-body #pp-docplan{margin-bottom:8px}
  #popup .aipanel textarea{min-height:96px}
  /* per-node context-source override rows: label left, tri-state select right */
  #popup .ctxrow{display:flex;align-items:center;justify-content:space-between;gap:12px;
    padding:7px 9px;border-radius:8px;font-size:13.5px;text-transform:none;letter-spacing:0;
    font-weight:400;color:var(--ink);margin:0}
  #popup .ctxrow:hover{background:var(--panel)}
  #popup select.ctx-tri{flex:0 0 auto;background:var(--panel);color:var(--ink);border:1px solid var(--line);
    border-radius:8px;padding:5px 8px;font:inherit;font-size:12.5px;outline:none;cursor:pointer}
  #popup select.ctx-tri:focus{border-color:var(--accent)}
  /* live-probe on/off — the one control in this panel with real-world side effects,
     so it reads as a switch rather than another row in the list */
  #popup .probe-toggle{flex:0 0 auto;min-width:52px;padding:5px 12px;font-size:12.5px}
  #popup .probe-toggle.on{background:var(--accent);border-color:var(--accent);color:#fff}
  #popup .liveblk{margin-top:2px}
  #popup .liveblk .fld{width:100%;background:var(--panel);border:1px solid var(--line);color:var(--ink);
    border-radius:8px;padding:7px 10px;font:inherit;font-size:13px;outline:none}
  #popup .liveblk .fld:focus{border-color:var(--accent)}
  /* modern, rounded documentation-status select (pill + custom caret + leading colored dot) */
  .status-field{display:flex;align-items:center;gap:9px}
  .status-field .status-dot{width:12px;height:12px;border-radius:50%;flex:0 0 auto;
    background:var(--sc,var(--status-not-started));box-shadow:0 0 0 3px color-mix(in srgb,var(--sc,var(--status-not-started)) 25%,transparent)}
  select.status-select{-webkit-appearance:none;-moz-appearance:none;appearance:none;flex:1;width:100%;
    background-color:var(--panel-2);color:var(--ink);border:1px solid var(--line);border-radius:999px;
    padding:10px 36px 10px 15px;font:inherit;font-size:14px;cursor:pointer;outline:none;transition:.15s;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239aa7b6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6 6-6'/></svg>");
    background-repeat:no-repeat;background-position:right 13px center}
  select.status-select:hover{border-color:var(--line-strong)}
  select.status-select:focus{border-color:var(--accent);box-shadow:0 0 0 3px color-mix(in srgb,var(--accent) 22%,transparent)}
  .tagpick{display:flex;flex-wrap:wrap;gap:8px}
  .chip{display:inline-flex;align-items:center;gap:7px;padding:8px 14px;border-radius:22px;font-size:13.5px;
    cursor:pointer;border:1px solid var(--line);color:var(--ink-dim);background:var(--panel-2);transition:.12s;user-select:none}
  .chip:hover{border-color:var(--line-strong)}
  .chip .cd{width:10px;height:10px;border-radius:50%;flex:0 0 auto}
  .chip.on{color:#fff;border-color:transparent}
  /* A selected chip takes the colour as its BACKGROUND, which left the dot invisible —
     same colour on same colour. It goes white with the label (V0.42.4). `!important`
     because every call site sets the dot's colour inline, which a plain rule loses to. */
  .chip.on .cd{background:#fff!important}
  /* A chip some other choice has switched off: readable, plainly inert. */
  /* Dimmed, NOT disabled (V0.48.1). Only the documentation-plan kinds wear this, and while
     "No documentation expected" is lit, pressing one is exactly how a person changes their
     mind — pointer-events:none made that the one thing they could not do. */
  .chip.off{opacity:.34}
  /* "No documentation expected" — the one plan choice that overrides the other four, so it
     is red and it switches them off (V0.42.4). The hex is written here rather than added to
     :root, which theme/tokens.test.ts holds to exactly the MUI token set; it is the same
     #ff7a7a the danger buttons already use. */
  .chip.plan-none{border-color:color-mix(in srgb,#ff7a7a 40%,transparent);
    background:color-mix(in srgb,#ff7a7a 12%,transparent);color:#ff8f8f}
  /* The resting fill is TRANSLUCENT, so what sits behind the label is the panel, not the red:
     #ff8f8f reads on the dark one and disappears on the light one. This is the only place the
     two themes need different ink (V0.42.6). The selector is `.light .chip…`, not the `.light`
     token block, so `theme/tokens.test.ts` — which compares that block exactly — is unaffected. */
  .light .chip.plan-none{color:#b3352c}
  .chip.plan-none:hover{border-color:#ff7a7a}
  /* SELECTED IS OPAQUE, which is what every other selected chip already is. It was a 26% wash
     with near-white ink: fine over the dark panel, invisible over the light one, where the mix
     composites to pale pink. Solid #ff7a7a with near-black ink is the same pairing
     `.docpick-del-go` already uses, so it keeps the light-red identity and reads on both. */
  .chip.plan-none.on{background:#ff7a7a;border-color:#ff7a7a;color:#231014;font-weight:600}
  /* "Custom" and its count, stepped from either side (V0.42.5). A native number spinner
     stacks two arrows inside the chip's height, which leaves them about 6px each; these sit
     beside the value instead. The chip keeps its siblings' height for free — .tagpick is a
     flex row, so every chip on a line stretches to the tallest. */
  .chip.plan-custom{gap:6px;padding:5px 8px}
  .chip.plan-custom .plan-custom-lbl{padding:0 1px}
  .plan-step{width:22px;height:22px;flex:0 0 auto;border:1px solid var(--line);
    background:var(--panel);color:var(--ink-dim);border-radius:6px;cursor:pointer;font:inherit;
    font-size:14px;line-height:1;display:inline-flex;align-items:center;justify-content:center;
    transition:.12s;padding:0}
  .plan-step:hover:not(:disabled){color:var(--ink);border-color:var(--line-strong)}
  .plan-step:disabled{opacity:.4;cursor:default}
  #pp-plan-custom{width:34px;text-align:center;background:var(--panel-2);
    border:1px solid var(--line);color:var(--ink);border-radius:6px;padding:2px 4px;font:inherit;
    font-size:12px;outline:none}
  #pp-plan-custom:focus{border-color:var(--accent)}
  /* A .btn carrying only its icon: the label's share of the padding goes with it. */
  .btn.icon-only{padding:8px 10px}
  .chip.ro{cursor:default}

  /* ---- the node popup's tag picker (V0.51) -------------------------------------------
     `#pp-tags` was a flex-wrapped wall of every tag in the installation, in the server's key
     order, with the hierarchy invisible. It is two things now and the border between them is
     the point: what this node CARRIES (a strip of filled chips, each with a ×), and what it
     COULD carry (the tree, in its own box, with a search). Everything here is scoped under
     `#popup` — `.chip` and `.tagpick` are shared with the documentation-plan row above. */
  #popup .tagcap{display:flex;align-items:center;gap:6px;margin:0 0 7px;font-size:11px;font-weight:600;
    letter-spacing:.03em;text-transform:uppercase;color:var(--ink-faint)}
  #popup .tagcap .n{font-weight:500;letter-spacing:0;text-transform:none;font-variant-numeric:tabular-nums}
  #popup .tagstrip{display:flex;flex-wrap:wrap;gap:8px}
  #popup .tagstrip .chip:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
  #popup .tagstrip-empty{font-size:13px;color:var(--ink-faint);font-style:italic}
  /* The vocabulary, boxed. `--panel` inside a `--panel-2` fold, so the box reads as a surface
     of its own rather than as more of the fold. */
  #popup .tagvocab{margin-top:14px;padding:11px 13px 13px;border:1px solid var(--line);
    border-radius:12px;background:var(--panel)}
  #popup .tagvocab-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:9px}
  #popup .tagvocab-head .tagcap{margin:0;flex:1 1 auto}
  #popup .tagsearch{display:inline-flex;align-items:center;gap:7px;flex:0 1 250px;min-width:150px;margin:0;
    padding:6px 10px;border:1px solid var(--line);border-radius:9px;background:var(--panel-2);
    text-transform:none;letter-spacing:0;font-weight:400}
  #popup .tagsearch:focus-within{border-color:var(--accent)}
  #popup .tagsearch .ico{width:14px;height:14px;color:var(--ink-faint)}
  #popup .tagsearch input{flex:1;min-width:0;border:none;background:none;outline:none;color:var(--ink);
    font:inherit;font-size:13px}
  #popup .tagsearch input::placeholder{color:var(--ink-faint)}
  #popup .tagvocab-empty{font-size:12.5px;color:var(--ink-faint);font-style:italic;padding:6px 2px}
  #popup .tagnote{margin-top:9px;font-size:11.5px;color:var(--ink-faint)}
  /* COLUMNS, not a grid: a group is a variable number of rows and CSS columns balance them
     without anybody counting. `column-width` rather than `column-count` so the same rule gives
     four columns at the edit shape's 980px and two on a narrow screen. */
  #popup .tagpick.cols{display:block;column-width:210px;column-gap:18px}
  #popup .taggroup{break-inside:avoid;display:flex;flex-direction:column;gap:1px;margin:0 0 10px}
  #popup .trow{display:flex;padding-left:calc(var(--d, 0) * 14px)}
  /* The row is `TagTree`'s row wearing `.chip`: same 14px caret cell, same dot, same indent
     step, because they are the same objects in the same app. `.chip`'s pill padding and radius
     are overridden, its `.on` fill is not — that is what a selected tag looks like everywhere. */
  #popup .chip.row{flex:1 1 auto;min-width:0;display:flex;align-items:center;gap:7px;padding:5px 7px;
    border-radius:8px;border-color:transparent;background:transparent;color:var(--ink);font-size:13.5px}
  #popup .chip.row:hover{background:var(--panel-2);border-color:transparent}
  #popup .chip.row.on{color:#fff}
  #popup .chip.row.on:hover{background:inherit}
  #popup .chip.row:focus-visible{outline:2px solid var(--accent);outline-offset:-2px}
  #popup .chip.row .nm{flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  #popup .tcaret{width:14px;flex:0 0 auto;padding:0;border:none;background:none;color:var(--ink-faint);
    font-size:10px;line-height:1;text-align:center;cursor:pointer}
  #popup .chip.row.on .tcaret{color:#fff}
  #popup .tcaret:hover{color:var(--ink)}
  #popup .chip.row.on .tcaret:hover{color:#fff}
  /* A folded group that hides an assignment says so, in the hidden tag's own colour. */
  #popup .tag-plus{flex:0 0 auto;margin-left:auto;padding:1px 6px;border-radius:9px;
    font-size:10px;font-weight:600;color:#fff;font-variant-numeric:tabular-nums}
  /* The × on a selected tag (V0.51) — `.subj-tok .x`'s manners, white on a filled chip. */
  #popup .chip .x{flex:0 0 auto;border:none;background:none;color:inherit;opacity:.55;cursor:pointer;
    font-size:14px;line-height:1;padding:0 1px 0 3px;border-radius:50%}
  #popup .chip .x:hover{opacity:1;color:#ff7a7a}
  #popup .chip.on .x:hover{opacity:1;color:#fff}
  #popup .chip .x:focus-visible{outline:2px solid var(--accent);outline-offset:1px;opacity:1}
  /* ---- to-dos (V0.50) -------------------------------------------------------------- *
   * The node popup's To-dos section and the "My items" pane share these, which is why they
   * are one block rather than two: the row shape, the overdue colour and the fold chevron are
   * the same object seen in two places, and two copies would drift the first time one moved.
   *
   * THE OVERDUE RED IS A LITERAL, deliberately — the precedent is .row-x's #ff7a7a and the
   * star's gold, and the comment there states the rule: a one-off accent is not a token. It is
   * NOT --status-doc-rejected, which is the same red and means something else entirely (a
   * document the AI declined); sharing the variable would tie a date to a workflow state. */
  /* `#popup label` sets display:block and out-specifies a bare class (id+element beats one
     class), so both label rules below carry the #popup prefix. Without it the count renders as
     inline text butted against the heading — "TO-DOS1/5" — which is what happened first. */
  /* The To-dos panel wears `.aipanel` (V0.50.1) and adds only what is its own. Two panels in
     the same form that fold differently are two things to learn instead of one. */
  .pp-fold{width:14px;height:14px;flex:0 0 auto;fill:none;stroke:currentColor;stroke-width:2;
    stroke-linecap:round;stroke-linejoin:round;transform:rotate(-90deg);transition:transform .15s ease}
  .pp-fold.open{transform:rotate(0deg)}
  /* `.pp-foldcount` was here until V0.51 — the To-dos count, right-aligned in its summary. It
     is `.pp-foldval.count` now, a `.count-pill` like every other section's figure; the rule went
     with its last user rather than being left to style nothing. */

  /* ---- what a fold's heading says (V0.51) --------------------------------------------
     Every section of the popup folds now, and its summary line is the section's ANSWER:
     shut, the items themselves as their own pills; open, one count pill in their place,
     because the items are on screen below and a heading repeating them is noise. Right
     edge, which is where `.pp-foldcount` has sat since V0.50.1 — the position was chosen
     against the alternative in a proof of concept. Built by components/PopupFold.tsx. */
  .pp-foldval{margin-left:auto;flex:0 1 auto;min-width:0;display:inline-flex;align-items:center;gap:6px;
    font-size:12.5px;font-weight:500;letter-spacing:0;text-transform:none;color:var(--ink-dim);
    overflow:hidden;justify-content:flex-end}
  /* The swap. `[hidden]` would be a second mechanism; the fold's own state is already in the DOM. */
  #popup .aipanel[open]>summary .pp-foldval.full{display:none}
  #popup .aipanel:not([open])>summary .pp-foldval.brief{display:none}
  .pp-foldval.count{font-variant-numeric:tabular-nums}
  .pp-foldval .count-pill{display:inline-flex;align-items:center;flex:0 0 auto;padding:2px 9px;
    border-radius:12px;border:1px solid var(--line-strong);background:var(--panel);
    color:var(--ink-dim);font-size:11px;font-weight:600;font-variant-numeric:tabular-nums}
  /* The mini pills: each is its own family's pill at summary size, so the heading shows the
     same objects the body does. Sizes only — colour comes inline from the tag/kind, as
     everywhere else. */
  .pp-foldval .chip.mini{flex:0 0 auto;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600;gap:5px;cursor:default}
  .pp-foldval .chip.mini .cd{width:7px;height:7px}
  .pp-foldval .docpill.mini{flex:0 0 auto;padding:2px 8px;border-radius:6px;font-size:11px;gap:4px;cursor:default;box-shadow:none}
  .pp-foldval .docpill.mini .ico{width:11px;height:11px}
  .pp-foldval .subj-tok.mini{flex:0 0 auto;padding:1px 7px 1px 2px;border-radius:14px;font-size:11px;gap:5px;font-weight:500}
  .pp-foldval .subj-tok.mini .subj-av{width:16px;height:16px;font-size:8px}
  /* Inherited from an ancestor: dashed, the language the doc bar's ownership box already uses. */
  .pp-foldval .subj-tok.mini.inh{border-style:dashed;color:var(--ink-dim)}
  /* The overflow wears its neighbours' class AND this one, so "+3" is one more pill of the
     same kind rather than a footnote — and neutral, because it names no single thing. */
  .pp-foldval .plus{background:var(--panel);border:1px solid var(--line-strong);color:var(--ink-dim);
    font-variant-numeric:tabular-nums;font-weight:600}
  .pp-foldval .chip.mini.plus .cd,.pp-foldval .docpill.mini.plus .ico{display:none}
  /* `.aipanel-body` pads 0 14px 14px; the list wants its rows to reach a little wider than the
     prose fields do, and its own small gap between them. */
  #popup .todopanel .aipanel-body{padding:2px 8px 10px}

  /* The pane's fold controls (V0.50). `.myitems-title-flex` already lays its children out with
     `justify-content:space-between`, so the chevron needs no margin of its own — it is simply
     the last child. */
  .myitems-fold{border:none;background:none;padding:0 2px;color:var(--ink-faint);cursor:pointer;
    display:inline-flex;align-items:center;line-height:1}
  .myitems-fold:hover{color:var(--ink-dim)}
  .myitems-fold .pp-fold{width:13px;height:13px}
  /* Only the eye needs pushing right; without this the chevron would take the free space and
     the two controls would sit at opposite ends of the heading. */
  .myitems-title-flex .myitems-eye{margin-left:auto}

  .todogroup{margin-bottom:2px}
  .todogroup-head{display:flex;align-items:center;gap:5px;padding:3px 7px 2px}
  .todogroup-fold{border:none;background:none;padding:0;color:var(--ink-faint);cursor:pointer;
    display:inline-flex;align-items:center;line-height:1}
  .todogroup-fold .pp-fold{width:12px;height:12px}
  /* The label NAVIGATES and the chevron folds — two targets, see MyTodos.tsx. The pointer and
     the hover colour are what say so before somebody clicks. */
  .todogroup-name{display:flex;align-items:center;gap:6px;flex:1;min-width:0;cursor:pointer;
    font-size:12px;font-weight:600;color:var(--ink-dim);overflow:hidden;text-overflow:ellipsis;
    white-space:nowrap}
  .todogroup-name:hover{color:var(--ink)}
  .todogroup-name .dot{width:9px;height:9px;border-radius:50%;flex:0 0 auto;
    border:1px solid rgba(0,0,0,.15)}
  .todogroup-count{flex:0 0 auto;font-size:11px;color:var(--ink-faint)}
  /* In the pane the rows are one indent in from their group heading, and tighter than in the
     popup — the sidebar is 260px and every pixel of title is worth having. */
  .todogroup .todorow{padding:3px 7px 3px 20px;gap:7px}
  .todogroup .todo-title{font-size:12px}
  /* The whole row navigates here (V0.50.1) — the popup is where a to-do can actually be read,
     so the pane's job is to point at it. The checkbox stops the event; see MyTodos.tsx. */
  .todorow.open-todo{cursor:pointer}
  .todorow.open-todo:hover{background:var(--panel-2)}
  .todorow.open-todo:focus-visible{outline:2px solid var(--accent);outline-offset:-2px}
  .todogroup .todo-caret{width:10px;height:10px}

  .todolist{display:flex;flex-direction:column;gap:2px}
  /* THE SHARED ROW, one line, for the "My tasks" pane and the Projects tab. The node popup's
     own two-line shape is scoped to `#pp-todos` further down — V0.51.1 wrote it here by
     mistake and silently re-laid out both of the others. */
  .todorow{display:flex;align-items:center;gap:8px;padding:5px 7px;border-radius:8px;
    border-top:2px solid transparent}
  .todorow:hover{background:var(--panel-2)}
  .todorow[draggable='true']{cursor:grab}
  .todo-check{flex:0 0 auto;accent-color:var(--accent);cursor:pointer;margin:0}
  .todo-check:disabled{cursor:default;opacity:.45}
  .todo-title{flex:1;min-width:0;font-size:13px;color:var(--ink);overflow:hidden;
    text-overflow:ellipsis;white-space:nowrap;display:flex;align-items:center;gap:5px}
  /* THESE THREE ARE SHARED, AND V0.51.1 BRIEFLY DELETED THEM BY MISTAKE.
     The node popup stopped using them — its rows carry a `.row-caret` and a `.todo-detail`
     card — but `.todo-caret` is still drawn by the "My tasks" pane, by the Projects tab's TASK
     rows and by its NODE TREE rows, and `.todo-notes` by the Projects tab. Removing them left
     those `<svg>`s with no width or height at all, so each one rendered at the SVG default
     replaced size: a 1026px chevron that pushed its own row's title off the screen. An SVG with
     no intrinsic size is not a small icon, it is an enormous one. */
  .todo-title.expandable{cursor:pointer}
  .todo-title.expandable:hover{color:var(--accent)}
  .todo-title:focus-visible{outline:2px solid var(--accent);outline-offset:2px;border-radius:4px}
  .todo-caret{width:11px;height:11px;flex:0 0 auto;fill:none;stroke:currentColor;stroke-width:2.5;
    stroke-linecap:round;stroke-linejoin:round;color:var(--ink-faint);
    transform:rotate(-90deg);transition:transform .15s ease}
  .todo-caret.open{transform:rotate(0deg)}
  /* The description, once opened. Indented to the title's own left edge so it reads as
     belonging to that row rather than to the list, and `pre-wrap` because somebody typed it
     with the line breaks they meant. */
  .todo-notes{margin:0 7px 6px 30px;padding:7px 9px;border-left:2px solid var(--line-strong);
    background:var(--panel);border-radius:0 8px 8px 0;font-size:12px;line-height:1.5;
    color:var(--ink-dim);white-space:pre-wrap;word-break:break-word}
  .todorow.done .todo-title{color:var(--ink-faint);text-decoration:line-through}
  .todo-who{width:20px;height:20px;font-size:9px}
  .todo-due{flex:0 0 auto;font-size:11px;color:var(--ink-faint);white-space:nowrap}
  .todo-due.overdue{color:#ef4444;font-weight:600}
  .todorow.done .todo-due{color:var(--ink-faint);font-weight:400}
  /* `.row-x` is styled per host (.tagrow, .itemrow) and had no rule here, so the delete cross
     drew at full opacity in the app's body colour — a permanent × beside every to-do. Both
     controls share .itemrow's hover-reveal manners. */
  .todorow .row-x{opacity:0;color:var(--ink-faint);cursor:pointer;font-size:15px;line-height:1;
    padding:0 2px}
  .todorow:hover .row-x{opacity:.6}
  .todorow .row-x:hover{opacity:1;color:#ff7a7a}
  .todorow .row-edit{opacity:0;color:var(--ink-faint);cursor:pointer;line-height:1;padding:0 2px;
    display:inline-flex;align-items:center}
  .todorow .row-edit svg{width:13px;height:13px;fill:none;stroke:currentColor;stroke-width:2;
    stroke-linecap:round;stroke-linejoin:round}
  .todorow:hover .row-edit{opacity:.6}
  .todorow .row-edit:hover{opacity:1;color:var(--ink)}
  .todo-empty{font-size:12px;color:var(--ink-faint);padding:2px 7px 6px;line-height:1.45}
  .todo-error{font-size:12px;color:#ef4444;padding:4px 7px}
  /* SIZED LIKE THE POPUP'S OTHER FREE-TEXT FIELDS (V0.50.1) — `#pp-name`'s padding, radius and
     15px, not a cramped strip. It is where a to-do is written, so it should look as writable as
     the description above it. `--panel` rather than `--panel-2`, because the panel it now sits
     inside is itself `--panel-2` and a field the same colour as its card reads as disabled. */
  input.todo-add{width:100%;background:var(--panel);border:1px solid var(--line);color:var(--ink);
    border-radius:10px;padding:11px 13px;font:inherit;font-size:15px;outline:none;margin-top:6px}
  input.todo-add:focus{border-color:var(--accent)}
  input.todo-add::placeholder{color:var(--ink-faint)}

  .todoedit{display:flex;flex-direction:column;gap:7px;padding:8px;border:1px solid var(--line);
    border-radius:10px;background:var(--panel-2)}
  .todoedit input,.todoedit textarea{background:var(--panel);border:1px solid var(--line);
    color:var(--ink);border-radius:8px;padding:7px 9px;font:inherit;font-size:13px;outline:none}
  .todoedit input:focus,.todoedit textarea:focus{border-color:var(--accent)}
  .todoedit-notes{min-height:52px;resize:vertical}
  .todoedit-meta{display:flex;align-items:center;gap:10px}
  /* Same specificity problem, same fix: this one would otherwise inherit the section-heading
     look — block, uppercase, and an 18px top margin — inside a compact editor. */
  #popup label.todoedit-due{display:flex;align-items:center;gap:7px;font-size:12px;
    color:var(--ink-dim);margin:0;text-transform:none;letter-spacing:0;font-weight:500}
  .todoedit-foot{display:flex;justify-content:flex-end;gap:8px}
  .todoedit-foot .pbtn{padding:6px 14px;font-size:13px}

  /* ---- TASKS: the row, the card, the hierarchy, the drag (V0.51.1) -------------------- */
  /* THE INDENT IS ONE VARIABLE and everything derives from it: the children's offset, the
     elbow's arm, the indented composer. 40px is the user's own choice from the proof of
     concept — wide enough that a sub-task reads as belonging to the row above rather than as
     a slightly-tabbed sibling. Change it here and the connectors follow. */
  .todolist{--task-indent:40px}
  /* `.todoblock` stays the flex column it has always been for every surface; the popup's
     rows additionally need a positioning context for the elbow connectors. */
  .todoblock{display:flex;flex-direction:column}
  #pp-todos .todoblock{position:relative}

  /* TWO LINES (V0.51.1), AND ONLY IN THE NODE POPUP. A row is read far more often than it is
     acted on, so the second line carries what the first has no room to say;
     `align-items:flex-start` keeps the checkbox level with the title rather than centred
     against both lines.

     SCOPED TO `#pp-todos`, because `.todorow` is drawn by three surfaces and this shape belongs
     to one of them. Written as the bare `.todorow` first, it silently re-laid out the "My
     tasks" pane and the Projects tab — wrapping their single-line rows and top-aligning their
     avatars — which is the same mistake as deleting the shared caret above, in the other
     direction. The list a rule is FOR is the list it should name. */
  #pp-todos .todorow{display:flex;flex-wrap:wrap;align-items:flex-start;gap:8px;row-gap:2px;
    padding:6px 7px 5px;border-radius:8px;border-top:2px solid transparent;cursor:pointer;
    -webkit-user-select:none;user-select:none}
  /* A double-click must not select the title it is opening the editor for. */
  #pp-todos .todorow .todo-check{margin-top:2px}
  #pp-todos .todorow.sel{background:color-mix(in srgb,var(--accent) 12%,transparent)}
  #pp-todos .todorow.dragging{opacity:.4}
  #pp-todos .todorow:focus-visible{outline:2px solid var(--accent);outline-offset:-2px}
  /* The popup's own title wraps rather than ellipsizing: its row is two lines already, so a
     long title costs a third rather than being cut off where nobody can identify it. */
  #pp-todos .todo-title{display:block;overflow:visible;text-overflow:clip;white-space:normal;
    line-height:1.35;word-break:break-word}

  .todo-meta{flex:0 0 100%;display:flex;align-items:center;flex-wrap:wrap;gap:6px;min-width:0;
    padding-left:23px;font-size:11.5px;color:var(--ink-faint)}
  .todo-meta-part{display:inline-flex;align-items:center;gap:6px;min-width:0}
  .todo-meta .sep{color:var(--line-strong)}
  .todo-meta .todo-who{width:16px;height:16px;font-size:8px}
  .todo-meta .who-name{color:var(--ink-dim)}
  .todo-meta .todo-whos{display:inline-flex;align-items:center;gap:3px;min-width:0}
  .todorow.done .todo-meta{opacity:.7}
  .todo-note-mark{display:inline-flex;color:var(--ink-faint)}
  .todo-note-mark svg{width:12px;height:12px;fill:none;stroke:currentColor;stroke-width:2;
    stroke-linecap:round;stroke-linejoin:round}
  /* A sub-task due after the job it belongs to. Flagged, never forbidden — plans are written
     before they are understood — so it is a tone, not an error. */
  .todo-due.soon{color:var(--status-needs-update)}
  .todo-due.conflict{color:var(--status-needs-update)}

  /* WHO WROTE IT (V0.51.1): outlined with a pen, so it never reads as an assignee's filled
     avatar. The full name and the day ride the tooltip; the row has room for a first name. */
  .todo-author{display:inline-flex;align-items:center;gap:4px;flex:0 0 auto;
    padding:1px 7px 1px 5px;border-radius:10px;border:1px solid var(--line-strong);
    font-size:11px;color:var(--ink-dim);white-space:nowrap}
  .todo-author svg{width:10px;height:10px;stroke:currentColor;stroke-width:2;fill:none;
    stroke-linecap:round;stroke-linejoin:round;color:var(--ink-faint)}
  .todorow.done .todo-author{opacity:.6}

  /* The caret cell is drawn on every ROOT, blank where there are no sub-tasks, so titles line
     up down the list instead of stepping in and out by fourteen pixels. */
  .todorow .row-caret{width:14px;height:14px;flex:0 0 auto;display:inline-flex;align-items:center;
    justify-content:center;border:none;background:none;padding:0;margin-top:2px;cursor:pointer;
    color:var(--ink-faint)}
  .todorow .row-caret svg{width:11px;height:11px;fill:none;stroke:currentColor;stroke-width:2.5;
    stroke-linecap:round;stroke-linejoin:round;transform:rotate(-90deg);transition:transform .15s ease}
  .todorow .row-caret.open svg{transform:rotate(0)}
  .todorow .row-caret.blank{visibility:hidden}

  .todo-pill{flex:0 0 auto;padding:1px 7px;border-radius:999px;background:var(--panel-2);
    border:1px solid var(--line);font-size:11px;color:var(--ink-dim);font-variant-numeric:tabular-nums}
  .todo-pill.all{border-color:var(--accent);color:var(--accent)}

  .todo-acts{display:inline-flex;align-items:center;gap:2px;flex:0 0 auto}
  .todorow .row-add{width:20px;height:20px;display:inline-flex;align-items:center;justify-content:center;
    border:none;background:none;padding:0;border-radius:5px;color:var(--ink-faint);cursor:pointer;opacity:0}
  .todorow .row-add svg{width:12px;height:12px;fill:none;stroke:currentColor;stroke-width:2.2;
    stroke-linecap:round}
  .todorow:hover .row-add,.todorow:focus-within .row-add{opacity:1}
  .todorow .row-add:hover{background:var(--panel-2);color:var(--accent)}

  /* ---- the detail card ---- */
  /* EVERY row has one, for every reader: who a job is for and when it is wanted is not an
     editing fact. It sits between the row and the sub-tasks, so a parent reads as "the job,
     what it is, then its steps". */
  .todo-detail{margin:2px 0 4px 23px;padding:9px 11px;border:1px solid var(--line);
    border-radius:9px;background:var(--panel-2);font-size:12px;color:var(--ink-dim);cursor:default}
  .todo-detail-notes{white-space:pre-wrap;color:var(--ink);margin-bottom:8px;font-size:12.5px}
  .todo-detail-notes i{color:var(--ink-faint)}
  .todo-detail .kv{display:flex;flex-direction:column;gap:3px}
  .todo-detail .kv-row{display:flex;gap:8px;align-items:baseline}
  .todo-detail .kv b{flex:0 0 84px;font-weight:600;color:var(--ink-faint);font-size:11px;
    text-transform:uppercase;letter-spacing:.04em}
  .todo-detail .kv span{min-width:0;color:var(--ink)}
  .todo-detail .kv .sep{color:var(--line-strong);margin:0 5px}
  .todo-detail .kv .todo-who{width:16px;height:16px;font-size:8px;margin-right:2px}
  .todo-detail-foot{display:flex;align-items:center;gap:10px;margin-top:9px}
  .todo-detail-foot .pbtn{padding:4px 10px;font-size:12px;display:inline-flex;align-items:center;gap:5px}
  .todo-detail-foot .pbtn .ico{width:12px;height:12px;fill:none;stroke:currentColor;stroke-width:2;
    stroke-linecap:round;stroke-linejoin:round}

  /* ---- the hierarchy: indent plus elbows ---- */
  /* The elbow's arm is the indent minus the checkbox's own offset, so the line meets the box
     rather than the text however wide the indent is set. */
  .todochildren{position:relative;display:flex;flex-direction:column;gap:2px;
    margin-left:var(--task-indent)}
  .todochildren .todoblock::before{content:'';position:absolute;left:calc(13px - var(--task-indent));
    top:-3px;width:calc(var(--task-indent) - 12px);height:17px;
    border-left:1px solid var(--line-strong);border-bottom:1px solid var(--line-strong);
    border-radius:0 0 0 6px;opacity:.8}
  .todochildren .todoblock:not(:last-child)::after{content:'';position:absolute;
    left:calc(13px - var(--task-indent));top:14px;bottom:-4px;
    border-left:1px solid var(--line-strong);opacity:.8}

  /* ---- the drag ---- */
  /* A GAP OPENS where the row will land — the user's choice over a line. It is drawn only when
     the drop would actually move something (`isNoopDrop`), which is the complaint V0.50 earned:
     an indicator that promises a move and delivers none teaches people not to trust it. */
  .todo-gap{height:30px;border:1px dashed var(--accent);border-radius:8px;
    background:color-mix(in srgb,var(--accent) 8%,transparent);margin:2px 0}
  /* The end-of-list target. It exists ONLY during a drag: there was no way to reach the bottom
     of a list before, which is the bug this release was asked to fix. */
  .todo-dropzone{height:14px;border-radius:8px;border:1px dashed transparent;margin:1px 0}
  .todo-dropzone.hot{border-color:var(--accent);
    background:color-mix(in srgb,var(--accent) 8%,transparent);height:30px}
  .todorow.dropinto{outline:2px dashed var(--accent);outline-offset:-2px;
    background:color-mix(in srgb,var(--accent) 12%,transparent)}

  /* ---- the composer, the confirm, the hint ---- */
  input.todo-add.child,input.todo-add.indent{border-style:dashed}
  input.todo-add.indent{margin-left:var(--task-indent);width:calc(100% - var(--task-indent))}
  .todo-confirm{display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:7px 9px;
    border:1px solid var(--status-doc-rejected);border-radius:9px;
    background:color-mix(in srgb,var(--status-doc-rejected) 10%,transparent);font-size:12.5px;color:var(--ink)}
  .todo-confirm .pbtn{padding:4px 10px;font-size:12px}
  .todo-confirm .pbtn.danger{background:var(--status-doc-rejected);border-color:var(--status-doc-rejected);color:#fff}
  .todo-hint{margin-top:8px;font-size:11px;color:var(--ink-faint);line-height:1.5}
  /* WHICH JOB A SUB-TASK IS A STEP OF, on the surfaces that list tasks out of context —
     "My tasks" and the Projects tab. Faint and ahead of the title, so it reads as the
     setting rather than as part of the name. */
  .todo-parent{color:var(--ink-faint);font-weight:400;flex:0 1 auto;min-width:0;
    overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .todo-assignme{padding:4px 10px;font-size:12px;margin-left:auto}

  /* ---- THE ONE DATE CONTROL (V0.51.1) ------------------------------------------------- */
  /* A button that opens a month grid, at all five date fields. It replaces `input[type=date]`,
     which each browser draws its own way, cannot carry a "tomorrow" shortcut, and cannot be
     made to sit inside a dark panel. See components/DatePicker.tsx for the rest of the case. */
  .due-btn{display:inline-flex;align-items:center;gap:6px;background:var(--panel-2);
    border:1px solid var(--line);color:var(--ink-dim);border-radius:8px;padding:4px 8px;
    font:inherit;font-size:12.5px;cursor:pointer;text-transform:none;letter-spacing:0;font-weight:500}
  .due-btn:hover{border-color:var(--line-strong);color:var(--ink)}
  .due-btn.on,.due-btn:focus-visible{border-color:var(--accent);color:var(--ink);outline:none}
  .due-btn.set{color:var(--ink)}
  .due-btn:disabled{opacity:.6;cursor:default}
  .due-btn .ico{width:13px;height:13px;flex:0 0 auto;fill:none;stroke:currentColor;stroke-width:1.8;
    stroke-linecap:round;stroke-linejoin:round;color:var(--ink-faint)}
  .due-btn-text{white-space:nowrap}
  .due-btn .clr{margin-left:2px;color:var(--ink-faint);font-size:14px;line-height:1;padding:0 2px;border-radius:4px}
  .due-btn .clr:hover{color:var(--ink);background:var(--line)}

  /* FIXED and portalled to <body>: a backdrop-filter is a containing block for position:fixed
     exactly as a transform is, and `.docbar` has one — so a popover drawn from inside a bar
     would be confined to that 56px strip. The popup's own body scrolls and would clip it.
     `left`/`top` are written by the component, which re-measures on scroll and resize. */
  .cal{position:fixed;z-index:80;width:258px;padding:10px;border:1px solid var(--line);
    border-radius:12px;background:var(--panel);box-shadow:0 12px 34px rgba(0,0,0,.45);
    font-size:12.5px;text-transform:none;letter-spacing:0;font-weight:400;color:var(--ink)}
  .light .cal{box-shadow:0 12px 34px rgba(15,23,42,.18)}
  .cal-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px}
  .cal-head b{font-weight:600;font-size:13px}
  .cal-nav{border:none;background:none;color:var(--ink-dim);width:24px;height:24px;border-radius:6px;
    cursor:pointer;display:grid;place-items:center;font-size:16px;line-height:1}
  .cal-nav:hover{background:var(--panel-2);color:var(--ink)}
  .cal-quick{display:flex;flex-wrap:wrap;gap:4px;margin-bottom:8px}
  .cal-quick button{border:1px solid var(--line);background:var(--panel-2);color:var(--ink-dim);
    border-radius:999px;padding:3px 9px;font:inherit;font-size:11.5px;cursor:pointer}
  .cal-quick button:hover{border-color:var(--accent);color:var(--ink)}
  .cal-quick button.clear{margin-left:auto;color:var(--ink-faint)}
  .cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:2px}
  .cal-grid .wd{font-size:10px;font-weight:600;color:var(--ink-faint);text-align:center;
    padding:2px 0 4px;letter-spacing:.03em}
  .cal-grid .d{height:28px;border:none;background:none;border-radius:7px;color:var(--ink);
    font:inherit;font-size:12.5px;cursor:pointer;font-variant-numeric:tabular-nums}
  .cal-grid .d:hover:not(:disabled){background:var(--panel-2)}
  .cal-grid .d.out{color:var(--ink-faint);opacity:.5}
  .cal-grid .d.today{box-shadow:inset 0 0 0 1px var(--accent);color:var(--accent);font-weight:600}
  .cal-grid .d.sel,.cal-grid .d.sel:hover{background:var(--accent);color:#fff;font-weight:600}
  .cal-grid .d.past{opacity:.55}
  .cal-grid .d.we{color:var(--ink-dim)}
  .cal-grid .d:disabled{opacity:.25;cursor:default}
  .cal-grid .d:focus-visible{outline:2px solid var(--accent);outline-offset:-2px}

  .popup-foot{display:flex;justify-content:flex-end;gap:10px;margin-top:24px}
  /* THE WAY OUT STAYS ON SCREEN (V0.51). `#popup` is `max-height:calc(100vh - 80px)` with its
     own scrollbar, and the editable body is comfortably longer than one screen — so Cancel and
     Save were below the fold and leaving the form meant scrolling to it. Edit shape only: the
     reading shape's single Close button is what `node-popup.png` photographs and it does not
     move. The negative margin takes the bar to the panel's own edges, and `padding-bottom:0`
     on the popup lets it rest flush when the body is scrolled to the end, with no jump. */
  #popup:not(.readonly){padding-bottom:0}
  #popup:not(.readonly) .popup-foot{position:sticky;bottom:0;z-index:2;margin:24px -28px 0;
    padding:12px 28px 22px;background:var(--panel);border-top:1px solid var(--line)}
  .pbtn{border:1px solid var(--line);background:var(--panel);color:var(--ink);font:inherit;font-size:14px;
    font-weight:500;padding:10px 20px;border-radius:10px;cursor:pointer}
  .pbtn.primary{background:var(--accent);border-color:var(--accent);color:#fff}
  .pbtn:hover{filter:brightness(1.08)}
  .node.tag-dim{opacity:.28}
  .node.tag-hit{box-shadow:0 0 0 3px var(--hl,#5b8cff),0 4px 18px rgba(0,0,0,.3)}
  .node.search-dim{opacity:.22}
  .node.search-hit{box-shadow:0 0 0 3px #ffb020,0 4px 20px rgba(255,176,32,.35);opacity:1}
  .node.docstatus-dim{opacity:.22}
  .node.docstatus-hit{box-shadow:0 0 0 3px var(--dshl,#2bb0d8),0 4px 20px rgba(0,0,0,.3);opacity:1}
  .node.chat-dim{opacity:.25}
  .node.chat-hit{box-shadow:0 0 0 3px var(--chl,#22c55e),0 4px 22px rgba(34,197,94,.4);opacity:1}
  /* The eye assignment filter (V0.42): the nodes carrying my assignments light in a distinct
     teal, the rest dim — the fifth highlight layer. */
  .node.assign-dim{opacity:.24}
  .node.assign-hit{box-shadow:0 0 0 3px #14b8a6,0 4px 22px rgba(20,184,166,.4);opacity:1}
  /* Real-time presence — avatar circles (initials + hover full name) */
  .avatar{display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;
    font-size:11px;font-weight:700;color:#fff;background:var(--accent);border:2px solid var(--panel);
    box-shadow:0 1px 3px rgba(0,0,0,.35);letter-spacing:.3px;line-height:1;flex:0 0 auto;user-select:none}
  .avatar.editing{box-shadow:0 0 0 2px var(--panel),0 0 0 4px #f59e0b}
  .avatar.more{background:var(--panel-2);color:var(--ink-dim)}
  /* An uploaded picture sits INSIDE the circle above, so every size, ring and margin rule that
     already applies to initials applies to it unchanged — the .avatar box is the only geometry
     there is. cover, not contain: a portrait must fill the circle rather than letterbox in it. */
  .avatar .avatar-img{width:100%;height:100%;border-radius:50%;object-fit:cover;display:block}
  .presence-stack{display:inline-flex;align-items:center;flex-direction:row-reverse;margin-right:2px}
  .presence-stack .avatar{margin-left:-9px;cursor:default;transition:margin .12s}
  .presence-stack:hover .avatar{margin-left:-3px}
  .presence-stack:empty{display:none}
  .node-presence{position:absolute;top:-13px;right:-8px;display:flex;flex-direction:row-reverse;z-index:4;pointer-events:auto}
  .node-presence .avatar{width:22px;height:22px;font-size:10px;margin-left:-8px}
  .node.root .node-presence{right:auto;left:calc(50% + 8px)}
  .doc-presence{display:inline-flex;align-items:center;flex-direction:row-reverse;margin:0 6px}
  .doc-presence .avatar{margin-left:-8px}
  .doc-presence:empty{display:none}
  /* AI-help chatbot dock (bottom-right, collapsed by default) */
  .chatdock{position:fixed;bottom:16px;right:64px;z-index:30}
  .chat-launch{display:flex;align-items:center;gap:8px;background:var(--accent);color:#fff;border:none;border-radius:999px;
    padding:11px 17px;font:inherit;font-weight:600;font-size:13.5px;cursor:pointer;box-shadow:0 6px 20px rgba(0,0,0,.3)}
  .chat-launch:hover{filter:brightness(1.05)}
  .chatdock.open .chat-launch{display:none}
  .chat-panel{display:none;flex-direction:column;position:fixed;bottom:16px;right:64px;width:370px;max-width:calc(100vw - 92px);
    height:470px;max-height:calc(100vh - 96px);background:var(--panel);border:1px solid var(--line);border-radius:14px;
    box-shadow:0 14px 44px rgba(0,0,0,.4);overflow:hidden}
  .chatdock.open .chat-panel{display:flex}
  .chat-head{display:flex;align-items:center;gap:8px;padding:11px 14px;border-bottom:1px solid var(--line);
    background:color-mix(in srgb,var(--panel) 92%,transparent)}
  .chat-title{font-family:var(--font-display);font-weight:600;font-size:14px;flex:1;display:flex;align-items:center;gap:8px;color:var(--accent)}
  .chat-log{flex:1;overflow:auto;padding:14px;display:flex;flex-direction:column;gap:12px;font-size:13.5px;line-height:1.5}
  .chat-empty{color:var(--ink-faint);font-size:13px;text-align:center;margin:auto;padding:16px}
  .chat-msg{max-width:92%}
  .chat-msg.user{align-self:flex-end;background:var(--accent);color:#fff;border-radius:12px 12px 3px 12px;padding:8px 12px;max-width:85%}
  .chat-msg.bot{align-self:flex-start;background:var(--panel-2);border:1px solid var(--line);border-radius:12px 12px 12px 3px;padding:10px 12px}
  .chat-msg.thinking{color:var(--ink-dim);font-style:italic}
  .chat-md p{margin:0 0 8px}.chat-md p:last-child{margin:0}
  .chat-md ul{margin:0 0 8px;padding-left:18px}.chat-md li{margin:2px 0}
  .chat-md code{background:var(--panel);padding:1px 5px;border-radius:5px;font-size:.9em}
  .chat-md a{color:var(--accent)}
  .chat-cites{display:flex;flex-wrap:wrap;gap:6px;margin-top:9px}
  .chat-cite{display:inline-flex;align-items:center;gap:5px;font-size:11.5px;font-weight:600;padding:3px 10px;border-radius:999px;cursor:pointer;
    background:color-mix(in srgb,var(--accent) 14%,transparent);color:var(--accent);border:1px solid color-mix(in srgb,var(--accent) 40%,transparent)}
  .chat-cite:hover{background:color-mix(in srgb,var(--accent) 26%,transparent)}
  .chat-cite .cd{width:8px;height:8px;border-radius:50%;background:var(--chl,#22c55e);flex:0 0 auto}
  .chat-input{display:flex;gap:8px;padding:10px;border-top:1px solid var(--line);align-items:flex-end}
  .chat-input textarea{flex:1;resize:none;max-height:120px;background:var(--panel-2);border:1px solid var(--line);color:var(--ink);
    border-radius:10px;padding:9px 11px;font:inherit;font-size:13.5px;outline:none;line-height:1.4}
  .chat-input textarea:focus{border-color:var(--accent)}
  .search-wrap{position:relative;display:flex;align-items:center;gap:6px;min-width:0;flex:0 1 auto}
  .searchbox{display:flex;align-items:center;gap:7px;background:var(--panel-2);border:1px solid var(--line);
    border-radius:10px;padding:6px 10px;width:280px;max-width:100%;min-width:0;flex:0 1 auto;overflow:hidden;transition:.15s}
  .searchbox:focus-within{border-color:var(--accent)}
  .searchbox .ico{width:15px;height:15px;color:var(--ink-faint);flex:0 0 auto}
  .searchbox input{border:none;background:transparent;color:var(--ink);font:inherit;font-size:13.5px;outline:none;width:100%;min-width:0}
  .searchbox input::placeholder{color:var(--ink-faint)}
  .searchbox .sclear{border:none;background:transparent;color:var(--ink-faint);cursor:pointer;padding:0;
    width:16px;height:16px;display:none;flex:0 0 auto}
  .searchbox .sclear:hover{color:var(--ink)}
  .searchbox.has .sclear{display:grid;place-items:center}
  .search-count{font-size:11px;color:var(--ink-faint);white-space:nowrap;min-width:auto}
  .sfilter-btn{border:1px solid var(--line);background:var(--panel-2);color:var(--ink-dim);width:32px;height:32px;
    border-radius:9px;cursor:pointer;display:grid;place-items:center;flex:0 0 auto;transition:.15s}
  .sfilter-btn:hover{color:var(--ink);border-color:var(--line-strong)}
  .sfilter-btn.on{border-color:var(--accent);color:var(--accent)}
  .filterpanel{position:absolute;top:calc(100% + 8px);right:0;z-index:35;background:var(--panel);border:1px solid var(--line);
    border-radius:12px;box-shadow:0 10px 30px rgba(0,0,0,.28);padding:6px;display:none;min-width:210px}
  .filterpanel.show{display:block}
  .filterrow{display:flex;align-items:center;gap:9px;padding:7px 9px;border-radius:8px;font-size:13px;cursor:pointer;user-select:none;color:var(--ink)}
  .filterrow:hover{background:var(--panel-2)}
  .filterrow input{accent-color:var(--accent);width:15px;height:15px;cursor:pointer;flex:0 0 auto}
  .filterrow.divider{border-bottom:1px solid var(--line);margin-bottom:4px;padding-bottom:9px}
  /* Reclaim width as the topbar tightens (small window / zoomed in) so the search
     bar never has to overlap the side buttons. */
  @media(max-width:1180px){
    .tb-left .btn span:not(.dot-ind),.tb-right .btn span{display:none}
    .editswitch{width:auto}
    .brand small{display:none}
  }
  @media(max-width:720px){.search-count{display:none}.user-chip{display:none}}
  /* .tb-center is also shown/hidden by the fit controller (features/topbar/topbarFit.ts) —
     but only as its LAST RESORT, once even a fully-overflowed bar leaves the search less
     than its minimum. Everything before that point moves controls into the "…" menus. */
  .node .meta{position:absolute;bottom:-6px;right:-4px;display:flex;gap:2px}
  .node .meta .m{width:9px;height:9px;border-radius:50%;background:var(--ink-faint)}
  /* full-screen documentation overlay */
  #docmodal{position:fixed;inset:0;z-index:74;background:var(--bg);display:none;flex-direction:column}
  #docmodal.show{display:flex}
  /* The doc bar mirrors the map's top bar (V0.44.1): three zones, the .tb-left / .tb-center /
     .tb-right shape above — edit controls left, the title centred where the search box sits,
     everything else right. The centre flexes and may shrink; the sides never do. */
  .docbar{display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--line);
    background:color-mix(in srgb,var(--panel) 90%,transparent);backdrop-filter:blur(10px)}
  .docbar-left,.docbar-right{display:flex;align-items:center;gap:10px;flex:0 0 auto}
  .docbar-center{flex:1 1 auto;display:flex;align-items:center;justify-content:center;gap:10px;min-width:0}
  .doctitle{font-family:var(--font-display);font-weight:600;font-size:15px;color:var(--ink);
    white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}
  /* doc viewer is a wrap-around canvas: page centered, parent left, current above the page's
     top-left, children down the right margin, mindmap-style wires routing around the page. */
  .docstage{flex:1;position:relative;overflow:hidden;background:var(--bg)}
  .docnav-wires{position:absolute;inset:0;pointer-events:none;z-index:2;overflow:visible}
  #docnav-grid{position:absolute;inset:0;display:grid;
    grid-template-columns:minmax(178px,1fr) minmax(0,var(--docpagew,var(--pagew))) minmax(230px,1fr);
    grid-template-rows:auto minmax(0,1fr);column-gap:56px;row-gap:14px;padding:14px 24px 0;align-items:start}
  #docnav-grid.no-nav{grid-template-columns:0 1fr 0;column-gap:0;padding:0}
  #docnav-grid.no-nav .docnav-parentgroup,#docnav-grid.no-nav .docnav-currentgroup,#docnav-grid.no-nav .docnav-children{display:none}
  /* The log panel keeps its own column when the nav band is collapsed — the history is
     useful precisely when you are concentrating on one page. */
  #docnav-grid.no-nav.with-logs{grid-template-columns:minmax(250px,1fr) minmax(0,var(--docpagew,var(--pagew))) 0;
    column-gap:32px;padding:14px 24px 0}
  #docnav-grid.no-nav.with-logs .doclogs{display:block!important}
  .doclogs{grid-column:1;grid-row:2;align-self:start;max-height:100%;overflow:auto;padding-right:4px;min-width:0}
  /* Clearance for the parent's floating pills. MEASURED (client/src/lib/docnav.ts computes it
     from the overlap; the doc-view region writes it here), so nothing in this stylesheet has
     to be kept in step with the pill height. */
  .doclogs{padding-top:var(--pdocs-h,0px)}
  .doclogs-head{display:flex;align-items:baseline;gap:8px;font-family:var(--font-display);font-size:16px;
    font-weight:600;color:var(--ink);margin:0 0 12px}
  .doclogs-loc{font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:var(--ink-faint);
    border:1px solid var(--line);border-radius:999px;padding:1px 7px}
  .doclogs-stale{font-size:12px;line-height:1.45;color:var(--ink);background:color-mix(in srgb,var(--status-needs-update,#e0762d) 14%,transparent);
    border:1px solid color-mix(in srgb,var(--status-needs-update,#e0762d) 40%,transparent);
    border-radius:9px;padding:7px 10px;margin:0 0 12px}
  /* Timeline: pills joined by a vertical rule, newest at the top. */
  .logpill{position:relative;display:block;width:100%;text-align:left;border:1px solid var(--line-strong);
    background:var(--panel-2);color:var(--ink);border-radius:10px;padding:9px 12px;font:inherit;font-size:13px;
    cursor:pointer;margin:0 0 18px}
  .logpill:hover{background:var(--panel);border-color:var(--accent)}
  .logpill:last-child{margin-bottom:0}
  .logpill:not(:last-child)::after{content:'';position:absolute;left:50%;bottom:-18px;width:1.5px;height:18px;
    background:var(--line-strong)}
  .logpill .lp-top{display:flex;align-items:center;gap:7px;font-weight:600;margin-bottom:2px}
  .logpill .lp-v{font-size:11.5px;padding:0 6px;border-radius:999px;background:var(--panel);border:1px solid var(--line)}
  .logpill .lp-date{font-size:11.5px;color:var(--ink-faint);font-weight:400}
  .logpill .lp-label{display:block;color:var(--ink-dim);font-size:12.5px;line-height:1.4}
  .logpill .lp-src{width:8px;height:8px;border-radius:50%;flex:0 0 auto}
  .logpill.src-ai_generated .lp-src{background:var(--accent)}
  .logpill.src-ai_translated .lp-src{background:#8b5cf6}
  .logpill.src-human_edit .lp-src{background:var(--ink-faint)}
  .logpill.src-restore .lp-src{background:var(--status-proofread,#0a7)}
  .logpill.src-ai_failed .lp-src{background:var(--status-doc-rejected,#d0483c)}
  .logpill .lp-warn{color:var(--status-needs-update,#e0762d);font-size:11.5px}
  /* Detail drawer, over the doc overlay. */
  #logdetail{position:fixed;top:0;right:0;bottom:0;width:min(560px,94vw);z-index:80;background:var(--bg);
    border-left:1px solid var(--line);display:flex;flex-direction:column;box-shadow:-14px 0 40px rgba(0,0,0,.18)}
  .logdetail-head{display:flex;align-items:center;gap:10px;padding:14px 18px;border-bottom:1px solid var(--line)}
  .logdetail-head span{flex:1;font-family:var(--font-display);font-weight:600}
  .logdetail-body{flex:1;overflow:auto;padding:18px}
  .ld-sec{margin:0 0 18px}
  .ld-sec h4{font-size:11.5px;text-transform:uppercase;letter-spacing:.04em;color:var(--ink-faint);margin:0 0 7px}
  .ld-kv{display:grid;grid-template-columns:auto 1fr;gap:4px 12px;font-size:13px}
  .ld-kv dt{color:var(--ink-faint)}
  .ld-pre{white-space:pre-wrap;font-size:13px;line-height:1.55;background:var(--panel-2);border:1px solid var(--line);
    border-radius:9px;padding:10px 12px}
  .ld-cov{width:100%;border-collapse:collapse;font-size:12.5px}
  .ld-cov td{border-bottom:1px solid var(--line);padding:6px 8px;vertical-align:top}
  .ld-badge{display:inline-block;font-size:11px;padding:1px 7px;border-radius:999px;white-space:nowrap}
  .ld-badge.done{background:color-mix(in srgb,var(--status-approved,#0a7) 18%,transparent)}
  .ld-badge.partial{background:color-mix(in srgb,var(--status-needs-update,#e0762d) 20%,transparent)}
  .ld-badge.skipped{background:color-mix(in srgb,var(--status-doc-rejected,#d0483c) 18%,transparent)}
  .ld-flags{display:flex;flex-wrap:wrap;gap:6px}
  .ld-flag{font-size:11.5px;border:1px solid var(--line);border-radius:999px;padding:2px 8px;color:var(--ink-faint)}
  .ld-flag.on{color:var(--ink);border-color:var(--accent);background:color-mix(in srgb,var(--accent) 12%,transparent)}
  #docnav-grid .docframe-wrap{grid-column:2;grid-row:2;align-self:stretch;justify-self:stretch;position:relative;overflow:hidden;z-index:1;border-radius:12px;display:flex;flex-direction:column}
  .doctitle-input{max-width:60vw;width:340px;font-family:var(--font-display);font-weight:600;font-size:15px}
  .docedit-toolbar{flex:0 0 auto;display:flex;flex-wrap:wrap;gap:6px;justify-content:center;padding:10px 16px;border-bottom:1px solid var(--line);background:var(--panel);z-index:2}
  .docedit-toolbar button{border:1px solid var(--line);background:var(--panel);color:var(--ink);border-radius:8px;padding:6px 11px;font:inherit;font-size:13px;cursor:pointer}
  .docedit-toolbar button.on{background:var(--accent);border-color:var(--accent);color:#fff}
  .docedit-content{flex:1;overflow:auto;background:#eceef1}
  .docedit-content .ProseMirror{max-width:940px;margin:32px auto;background:#fff;border:1px solid #e2e5e9;border-radius:10px;
    box-shadow:0 1px 2px rgba(0,0,0,.04),0 10px 34px rgba(0,0,0,.09);padding:48px 64px;min-height:60vh;outline:none;line-height:1.7;color:#1f2328}
  .doc-actions{display:flex;flex-wrap:wrap;align-items:center;gap:9px;padding:10px 13px;
    background:var(--panel);border-bottom:1px solid var(--line);flex:0 0 auto}
  .doc-standin{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:18px;padding:40px;text-align:center;background:var(--panel-2)}
  .doc-standin p{margin:0;font-size:15px;line-height:1.55;color:var(--ink-dim);max-width:460px}
  /* "bean style but bigger" — the same .docpill, sized for a page rather than a node. */
  .docpill-lg{padding:12px 20px;gap:10px;font-size:15px;border-radius:11px;
    box-shadow:0 2px 8px rgba(0,0,0,.18)}
  .docedit-typebar{display:flex;align-items:center;gap:10px;padding:10px 13px;
    background:var(--panel);border-bottom:1px solid var(--line);flex:0 0 auto;flex-wrap:wrap}
  .docedit-typebar label{font-size:12px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:var(--ink-faint)}
  .docedit-typebar .typehint{font-size:12.5px;color:var(--ink-faint)}
  .doclink-edit{max-width:940px;margin:32px auto;background:#fff;border:1px solid #e2e5e9;border-radius:10px;padding:34px 40px}
  .doclink-edit label{display:block;font-size:12px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:#57606a;margin:0 0 8px}
  .doclink-edit input{width:100%;font:inherit;font-size:14px;padding:9px 12px;border:1px solid #d0d7de;border-radius:8px;color:#1f2328;background:#fff}
  .doclink-edit p{margin:12px 0 0;font-size:13px;line-height:1.5;color:#57606a}
  .docedit-content .ProseMirror table{border-collapse:collapse;width:100%}
  .docedit-content .ProseMirror td,.docedit-content .ProseMirror th{border:1px solid #d0d7de;padding:6px 10px}
  .docnav-parentgroup{grid-column:1;grid-row:1;justify-self:stretch;align-self:center;z-index:3;position:relative;display:flex;flex-direction:column;align-items:flex-start}
  /* The parent's docs float below its bubble (out of flow) so the bubble stays centred on the
     wire spine. That float lands in the grid row BELOW, which is where .doclogs lives, so the
     block has to be bounded — PARENT_DOC_CAP in the doc-view region holds it to four pills,
     and the measured layout gives .doclogs exactly their overhang as top padding.
     Width: the group is stretched to its grid column (see .docnav-parentgroup) so 100% here
     means that column, and +52px crosses the 56px gutter leaving 4px before the page. With
     column 1 never narrower than 178px that is always >=230px, so it can never collide with
     the page and never needs a floor of its own. The ceiling stops a very wide screen
     turning these into vast bars. */
  .docnav-parentgroup .docnav-doccol{position:absolute;top:100%;left:0;margin-top:8px;
    width:min(calc(100% + 52px),420px)}
  .docnav-parentgroup .docnav-doccol .docnav-pill{max-width:100%;min-width:0}
  .docnav-currentgroup{grid-column:2;grid-row:1;justify-self:start;align-self:center;z-index:3;display:flex;align-items:center;gap:22px}
  .docnav-children{grid-column:3;grid-row:1 / span 2;justify-self:start;align-self:start;z-index:3;display:flex;flex-direction:column;gap:18px;max-height:100%;overflow:auto;padding-bottom:16px}
  #doc-frame{flex:1;width:100%;border:0;background:#fff;display:block}
  .doc-error{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:12px;text-align:center;padding:30px;color:var(--ink)}
  .doc-error p{margin:0;font-size:16px;font-weight:500}
  .doc-error .doc-error-sub{font-size:13.5px;color:var(--ink-dim);max-width:440px;font-weight:400}
  .docnav-node{position:relative;display:inline-flex;align-items:center;gap:9px;box-sizing:border-box;
    padding:9px 13px;border-radius:11px;border:1px solid;background:var(--panel);color:var(--ink);
    font-size:13.5px;font-weight:500;line-height:1.35;max-width:230px;cursor:pointer;
    box-shadow:0 2px 10px rgba(0,0,0,.14);transition:box-shadow .15s}
  .docnav-node:hover{box-shadow:0 4px 18px rgba(0,0,0,.24)}
  .docnav-node.root{font-family:var(--font-display);font-weight:600;font-size:15px;padding:12px 18px;color:#fff;border:none}
  .docnav-node.big{font-size:16px;font-weight:600;padding:15px 22px;gap:11px}
  .docnav-node.big .swatch{width:11px;height:11px}
  .docnav-node.active{outline:2px solid var(--accent);outline-offset:2px}
  .docnav-node .swatch{width:9px;height:9px;border-radius:50%;flex:0 0 auto}
  .docnav-node .txt{white-space:normal;word-break:break-word;overflow-wrap:anywhere}
  .docnav-toggle{margin-left:4px;width:19px;height:19px;border-radius:50%;border:1.5px solid var(--line-strong);
    background:var(--panel);color:var(--ink-dim);font-size:14px;line-height:1;display:grid;place-items:center;cursor:pointer;padding:0;flex:0 0 auto}
  .docnav-toggle:hover{background:var(--panel-2)}
  .docnav-doccol{display:flex;flex-direction:column;gap:5px;margin-top:6px;align-items:flex-start}
  .docnav-currentdocs{display:flex;flex-direction:column;justify-content:center;gap:16px}
  .docnav-pillrow{display:flex;flex-wrap:wrap;align-items:center;gap:7px;min-height:26px}
  /* ---- one documentation-pill format --------------------------------------------------
     Every documentation entry is this pill: a row under a node on the map (.beanrow), a pill
     in the doc-nav band (.docnav-pill), a button in the node popup (.docpill-block). Colour
     is set inline from DOC_BEAN_COLOR; only size comes from the modifier. The type icon
     inherits the pill's white text through .ico's stroke:currentColor, so it needs no colour
     of its own. Every one of them is built by client/src/components/DocPill.tsx — there is no
     second implementation left, and new pill kinds go there. */
  .docpill{display:inline-flex;align-items:center;gap:6px;box-sizing:border-box;
    border-radius:8px;border:1px solid transparent;color:#fff;font-family:inherit;font-weight:600;
    cursor:pointer;white-space:nowrap;overflow:hidden;
    transition:filter .15s,border-color .15s,box-shadow .15s}
  .docpill:hover{filter:brightness(1.08)}
  .docpill .lbl{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .docnav-pill{max-width:230px;padding:5px 13px;font-size:12px;box-shadow:0 1px 3px rgba(0,0,0,.16)}
  .docpill-block{width:100%;padding:9px 13px;gap:8px;font-size:13.5px;border-radius:9px;
    box-shadow:0 1px 3px rgba(0,0,0,.14)}
  .docnav-pill.create{background:transparent!important;color:var(--ink-dim);border:1px dashed var(--line-strong);box-shadow:none;font-weight:600}
  .docnav-pill.create:hover{border-color:var(--accent);color:var(--accent);filter:none}
  /* A PLANNED-but-missing pill (V0.42): dashed outline in the KIND'S colour, no fill, on all
     three surfaces (.beanrow, .docnav-pill, .docpill-block). Distinct from .create, which is
     the nav band's neutral "add a page to this empty slot". borderColor/color come inline. */
  .docpill.planned{background:transparent;border-style:dashed;box-shadow:none;opacity:.9}
  .docpill.planned:hover{opacity:1;filter:none;background:color-mix(in srgb,currentColor 10%,transparent)}
  .docnav-childitem{display:flex;flex-direction:column;gap:2px}
  .node .docbeans{position:absolute;bottom:-6px;left:-4px;display:flex}
  .node .docbeans .bean{width:19px;height:10px;border-radius:5px;position:relative;box-sizing:border-box;border:1px solid transparent;cursor:pointer}
  .node .docbeans .bean:nth-child(2){margin-left:-5px;z-index:2}
  .node .docbeans .bean:nth-child(3){margin-left:-5px;z-index:3}
  .node .docbeans .bean:nth-child(4){margin-left:-5px;z-index:4}
  .node .docbeans .bean-count{width:auto;min-width:19px;padding:0 3px;font-size:6.5px;line-height:10px;
    text-align:center;color:#fff;background:var(--ink-faint);margin-left:-6px;z-index:5}
  .node .docbeans.expanded{position:absolute;top:100%;left:0;bottom:auto;margin-top:4px;
    display:flex;flex-direction:column;gap:3px;width:230px;z-index:4}
  /* Size only — the look comes from .docpill. Height MUST stay 22px: ROW_H in
     client/src/lib/layout/constants.ts reserves exactly this per row, and the two silently
     desynchronise if they differ (nodes overlap, wires point at the wrong place). DocPill's
     `row` variant takes its height FROM that constant, and layout/docHeight.test.ts reads this
     rule and fails on drift. The 11px icon fits inside 22px. */
  .node .docbeans.expanded .beanrow{height:22px;padding:0 8px;gap:5px;width:100%;
    font-size:9.5px;font-weight:500}
  @media(max-width:720px){.brand small{display:none}.seg span{display:none}.seg{padding:8px}#sidebar{width:230px}.side-toggle{left:240px}}
  /* ---- signed-in user chip ---- */
  .user-chip{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;font-weight:500;color:var(--ink-dim);
    background:var(--panel-2);border:1px solid var(--line);border-radius:20px;padding:5px 12px;white-space:nowrap}
  .user-chip::before{content:"";width:7px;height:7px;border-radius:50%;background:var(--accent)}
  .user-chip:empty{display:none}
  /* ---- read-only documentation buttons in the node popup ---- */
  #pp-docs{display:flex;flex-direction:column;gap:9px}
  /* The widget's address, at the foot of the documentation section (V0.51): one line, its
     label inline, rather than a full-width field under a heading of its own. */
  #popup .pp-docloc{display:grid;grid-template-columns:auto 1fr;gap:4px 10px;align-items:center;
    margin-top:12px;padding-top:11px;border-top:1px solid var(--line)}
  #popup .pp-docloc > label{margin:0;white-space:nowrap}
  #popup .pp-docloc #pp-docpath-help{grid-column:2;margin-top:0}
  /* ONE ROW PER DOCUMENT (V0.43.2): the pill takes the room, its buttons sit to the right.
     Was a stacked block per SLOT with an uppercase heading over it — `.pp-doc-lbl`, now
     deleted, because a pill names its own document and an owed pill names its kind. */
  .pp-doc-field{display:flex;align-items:center;gap:8px}
  .pp-doc-field > .pbtn{flex:0 0 auto}
  /* Remove asks in the row itself, so the list behind it is never covered. */
  .pp-doc-field.pp-doc-asking{border:1px solid #ff7a7a;border-radius:10px;padding:8px 10px;
    background:color-mix(in srgb,#ff7a7a 9%,transparent)}
  .pp-doc-asktext{flex:1;min-width:0;font-size:13px;color:#ff8f8f}
  .light .pp-doc-asktext{color:#b3352c}
  .pp-doc-delete{background:#ff7a7a;border-color:#ff7a7a;color:#231014;font-weight:600}
  /* ---- login overlay ---- */
  #loginmodal{position:fixed;inset:0;z-index:80;background:var(--bg);display:none;align-items:center;justify-content:center;padding:20px}
  #loginmodal.show{display:flex}
  .login-card{width:360px;max-width:100%;background:var(--panel);border:1px solid var(--line);border-radius:18px;
    box-shadow:0 20px 60px rgba(0,0,0,.5);padding:30px 30px 26px}
  .login-card .brand{justify-content:center;margin-bottom:4px;font-size:18px}
  .login-sub{text-align:center;color:var(--ink-dim);font-size:13px;margin-bottom:20px}
  .login-card label{display:block;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.03em;
    color:var(--ink-faint);margin:16px 0 6px}
  .fld{width:100%;background:var(--panel-2);border:1px solid var(--line);color:var(--ink);
    border-radius:10px;padding:11px 13px;font:inherit;font-size:14px;outline:none}
  .fld:focus{border-color:var(--accent)}
  select.fld{cursor:pointer}
  .pwd-wrap{position:relative;display:flex;align-items:center}
  .pwd-wrap .fld{padding-right:40px}
  .pwd-toggle{position:absolute;right:6px;border:none;background:transparent;color:var(--ink-faint);
    cursor:pointer;padding:5px;display:grid;place-items:center}
  .pwd-toggle:hover{color:var(--ink)}
  .login-error{display:none;color:#ff7a7a;font-size:13px;margin-top:14px;text-align:center}
  .login-card .pbtn{width:100%;justify-content:center;margin-top:22px}
  /* ---- doc-page picker / Tiptap editor ---- */
  #docpickmodal{position:fixed;inset:0;z-index:80;background:var(--bg);display:none;flex-direction:column}
  #docpickmodal.show{display:flex}
  .docpick-row{display:flex;align-items:center;gap:10px;padding:10px 14px;border:1px solid var(--line);border-radius:10px;margin-bottom:8px}
  .docpick-row .ico{margin-right:0!important}
  .docpick-row .title{flex:1;font-size:14px}
  .docpick-row .kind{font-size:11px;color:var(--ink-faint);text-transform:uppercase}
  /* ---- the doc picker's create row and its delete affordance (V0.42.4) ---- */
  /* Create is the FIRST entry in the list rather than a block under it. Dashed and in the
     accent, so it reads as "the one that is not a page yet" beside the solid rows. */
  .docpick-createrow{display:flex;align-items:center;gap:10px;padding:11px 14px;
    border:1px dashed var(--accent);border-radius:10px;margin-bottom:8px;cursor:pointer;
    color:var(--accent);font-size:14px;font-weight:500;
    background:color-mix(in srgb,var(--accent) 8%,transparent);transition:.14s}
  .docpick-createrow:hover{background:color-mix(in srgb,var(--accent) 15%,transparent)}
  .docpick-createrow .ico{color:var(--accent)}
  .docpick-createbox{display:grid;gap:9px;padding:13px;margin-bottom:10px;border-radius:10px;
    border:1px solid var(--accent);background:color-mix(in srgb,var(--accent) 6%,transparent)}
  /* Deleting the PAGE, which is not what the button at the bottom of this dialog does — that
     one detaches and leaves the page alive for every other node using it. */
  .docpick-del{border:1px solid var(--line);background:var(--panel);color:var(--ink-faint);
    border-radius:9px;width:34px;height:34px;display:inline-flex;align-items:center;
    justify-content:center;cursor:pointer;transition:.15s;flex:0 0 auto}
  .docpick-del:hover{color:#ff7a7a;border-color:color-mix(in srgb,#ff7a7a 45%,transparent)}
  .docpick-row.confirming{border-color:#ff7a7a;
    background:color-mix(in srgb,#ff7a7a 9%,transparent)}
  .docpick-confirmtext{flex:1;font-size:13.5px;color:#ff8f8f}
  .docpick-del-go{background:#ff7a7a;border-color:#ff7a7a;color:#231014;font-weight:600}
  /* ---- users / groups / permissions page ---- */
  #permmodal{position:fixed;inset:0;z-index:70;background:var(--bg);display:none;flex-direction:column}
  #permmodal.show{display:flex}
  .perm-head{display:flex;align-items:center;gap:12px;padding:14px 18px;border-bottom:1px solid var(--line);
    background:color-mix(in srgb,var(--panel) 90%,transparent);backdrop-filter:blur(10px)}
  .perm-head h2{font-family:var(--font-display);font-size:16px;font-weight:600;flex:1;display:flex;align-items:center;gap:10px}
  .perm-wrap{flex:1;overflow:auto}
  /* THE shared page width. One width family for every centred reading surface: the document
     page column (#docnav-grid falls back to it when --docpagew is unset, which it always is
     today) and the admin panels below. Deliberately NOT in the first :root block — that block
     is the theme-token contract theme/tokens.test.ts holds against tokens.ts, and this is a
     layout constant with no MUI consumer. */
  :root{--pagew:980px}
  .perm-inner{max-width:var(--pagew);margin:0 auto;padding:18px 22px 60px}
  /* The permissions page is a WORK surface, not a reading column (V0.54.5): its tables carry a
     username, a display name, an e-mail, a password box, a role and an avatar per row, and at
     the 980px reading width every one of them was cut off. It takes the dashboard's wide
     measure (`#dashboardmodal.wide`) on every tab. */
  #permmodal .perm-inner{max-width:min(1400px,92vw)}
  @media(max-width:1100px){#permmodal .perm-inner{max-width:94vw}}
  .perm-tabs{display:flex;gap:8px;margin-bottom:20px}
  .perm-tab{border:1px solid var(--line);background:var(--panel);color:var(--ink-dim);font:inherit;font-size:13.5px;
    font-weight:500;padding:9px 18px;border-radius:10px;cursor:pointer;display:flex;align-items:center;gap:7px}
  .perm-tab:hover{color:var(--ink);border-color:var(--line-strong)}
  .perm-tab.on{background:var(--accent);border-color:var(--accent);color:#fff}
  #dashboardmodal{position:fixed;inset:0;z-index:70;background:var(--bg);display:none;flex-direction:column}
  #dashboardmodal.show{display:flex}
  /* ---- AI doc generation: a dashboard tab since V0.44.1 (#aidocmodal is gone) ---- */
  #aidoccfgmodal{position:fixed;inset:0;z-index:72;background:var(--bg);display:none;flex-direction:column}
  #aidoccfgmodal.show{display:flex}
  #targetsmodal{position:fixed;inset:0;z-index:72;background:var(--bg);display:none;flex-direction:column}
  #targetsmodal.show{display:flex}
  /* The language picker rides in every full-screen header, styled as a button so it sits
     with the other header actions rather than looking like a stray form field. */
  select.lang-picker{flex:0 0 auto;cursor:pointer;padding-right:8px;max-width:150px}
  .perm-head select.lang-picker{margin-left:auto}
  .perm-head select.lang-picker~button{margin-left:0}
  .docbar select.lang-picker{margin-left:4px}
  .logs-controls{display:flex;flex-wrap:wrap;align-items:center;gap:10px;margin:0 0 16px}
  .logs-sortable{cursor:pointer;user-select:none}
  .logs-sortable:hover{color:var(--accent)}
  .logs-sortable.on::after{content:' ▾';font-size:10px}
  .logs-sortable.on.asc::after{content:' ▴'}
  .logrow-src{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:7px;vertical-align:middle}
  .logs-link{background:none;border:none;padding:0;font:inherit;color:var(--accent);cursor:pointer;text-align:left}
  .logs-link:hover{text-decoration:underline}
  /* The document log is the widest read surface in the app — 8 columns — and it is a view of
     the dashboard's Logs tab since V0.49. The width and the sideways scroll it needs are
     already the `wide` host's (#dashboardmodal.wide), which every table tab takes; what stays
     here is the CLAMP: long Document and Change text cut to a readable width, with the full
     text one click away in the detail drawer. That clamp is the rule `e2e/25-logs.spec.ts`
     measures — the table's right edge must sit inside its card's. */
  .logs-cell-clamp{max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

  /* ---- the ACTION LOG's table (V0.49, #dash-log-tab) ------------------------------------
     Scoped to the tab, like #dash-docs-tab's rules, so nothing here can reach the document
     history beside it. Seven columns, so it takes the `wide` host's width and its card's
     sideways scroll — the same treatment every table tab gets. */
  #dash-log-tab .al-dates{display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--ink-faint)}
  #dash-log-tab .al-dates .fld{padding:7px 9px;font-size:12.5px;width:auto}
  #audit-table{min-width:1040px;table-layout:fixed}
  #audit-table th{white-space:nowrap}
  #audit-table td{vertical-align:top}

  /* SEVERITY READS AS FORM, not only as a word: a stripe down the row's leading edge, so
     what needs attention is findable while scrolling rather than only while reading. */
  #audit-table tr.al-row > td:first-child{border-left:3px solid transparent;padding-left:12px}
  #audit-table tr.al-row.bad > td:first-child{border-left-color:var(--status-doc-rejected,#ef4444)}
  #audit-table tr.al-row.warn > td:first-child{border-left-color:var(--review-in-review)}
  #audit-table tr.al-row.open > td{background:color-mix(in srgb,var(--accent) 7%,transparent)}

  #dash-log-tab .al-when{white-space:nowrap;font-variant-numeric:tabular-nums}
  #dash-log-tab .al-when .t{display:block;font-size:11.5px;color:var(--ink-faint)}

  /* Who — the actor KIND is a colour as well as a word, because 'the AI did this' and 'a
     person did this' is the distinction a reader scans for first. */
  #dash-log-tab .al-who{display:flex;align-items:center;gap:8px;min-width:0;background:none;
    border:none;padding:0;font:inherit;color:inherit;cursor:pointer;text-align:left;max-width:100%}
  #dash-log-tab .al-who:hover .al-whoname{color:var(--accent)}
  #dash-log-tab .al-av{width:24px;height:24px;border-radius:50%;flex:0 0 auto;display:grid;
    place-items:center;font-size:9.5px;font-weight:700;color:#fff;background:var(--ink-faint)}
  #dash-log-tab .al-av.ai{background:var(--accent)}
  #dash-log-tab .al-av.system{background:var(--internal-color)}
  #dash-log-tab .al-av.api-key{background:var(--demo-color)}
  #dash-log-tab .al-av.anonymous{background:transparent;border:1.5px dashed var(--line-strong);color:var(--ink-faint)}
  #dash-log-tab .al-whoname{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  #dash-log-tab .al-kind{display:block;font-size:11px;color:var(--ink-faint)}

  /* Action — the reader's label, with the stored verb beneath it. The key is shown because
     it is what the filters and the API speak, and hiding it would make them unguessable. */
  #dash-log-tab .al-act{background:none;border:none;padding:0;font:inherit;color:var(--ink);
    cursor:pointer;text-align:left;font-weight:500}
  #dash-log-tab .al-act:hover{color:var(--accent)}
  #dash-log-tab .al-key{display:block;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
    font-size:10.5px;color:var(--ink-faint);margin-top:2px}

  #dash-log-tab .al-chip{display:inline-flex;align-items:center;gap:7px;max-width:100%;
    padding:4px 10px;border:1px solid var(--line);border-radius:8px;background:var(--panel-2);
    color:var(--ink);font:inherit;font-size:12.5px;cursor:pointer}
  #dash-log-tab .al-chip:hover{border-color:var(--line-strong)}
  #dash-log-tab .al-chip .ty{font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:var(--ink-faint)}
  #dash-log-tab .al-chip-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  #dash-log-tab .al-on-crumb{display:block;font-size:11px;color:var(--ink-faint);margin-top:4px;
    overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  #dash-log-tab .al-none{color:var(--ink-faint)}
  #dash-log-tab .aidoc-node{font-size:12.5px;padding:5px 11px}

  /* Outcome — SEMANTIC colour, deliberately not the accent. `cursor:help` because the pill
     carries the status code it came from on hover (lib/auditLog.ts statusHint). A native
     title rather than a styled tooltip: the card scrolls sideways, which clips a child. */
  #dash-log-tab .al-out{display:inline-flex;align-items:center;padding:2px 9px;border-radius:999px;
    font-size:11.5px;font-weight:600;white-space:nowrap;cursor:help;border:1px solid var(--c);
    color:var(--c);background:color-mix(in srgb,var(--c) 12%,transparent)}
  #dash-log-tab .al-out.good{--c:var(--review-approved)}
  #dash-log-tab .al-out.warn{--c:var(--review-in-review)}
  #dash-log-tab .al-out.bad{--c:var(--status-doc-rejected,#ef4444)}
  #dash-log-tab .al-sum{line-height:1.5}

  #dash-log-tab .al-exp{border:1px solid var(--line);background:var(--panel-2);color:var(--ink-dim);
    border-radius:7px;width:26px;height:26px;display:grid;place-items:center;cursor:pointer;padding:0}
  #dash-log-tab .al-exp:hover{color:var(--ink);border-color:var(--line-strong)}
  #dash-log-tab .al-exp .ico{width:13px;height:13px;transition:transform .15s}
  #audit-table tr.al-row.open .al-exp .ico{transform:rotate(180deg)}
  @media(prefers-reduced-motion:reduce){#dash-log-tab .al-exp .ico{transition:none}}

  #dash-log-tab .al-detail td{background:var(--panel-2);padding:0}
  #dash-log-tab .al-dwrap{padding:14px 16px 16px;display:grid;gap:16px;
    grid-template-columns:repeat(auto-fit,minmax(240px,1fr))}

  /* ---- facets: what each value would return, before it is clicked -------------------- */
  #audit-facets{display:grid;gap:6px;margin:0 0 14px}
  #dash-log-tab .audit-facet-row{display:flex;flex-wrap:wrap;align-items:center;gap:6px}
  #dash-log-tab .audit-facet-label{font-size:11px;text-transform:uppercase;letter-spacing:.04em;
    color:var(--ink-faint);font-weight:600;min-width:52px}
  #dash-log-tab .audit-facet{font-size:12px;padding:4px 10px 4px 8px}
  /* A zero stays on screen, greyed: a facet list that drops its empty values changes shape
     as you filter, so the chip you were reaching for moves out from under the cursor. */
  #dash-log-tab .audit-facet.empty{opacity:.45;cursor:default}
  #dash-log-tab .audit-facet.empty:hover{border-color:var(--line);color:var(--ink-dim)}
  /* THE DOT NEEDS ITS OWN BASE RULE HERE. `.dt-dot` is shaped by a selector scoped to
     `#dash-docs-tab` and `.dt-pop`, so inside this tab the span had no size, no shape and no
     background — the tone classes below were setting a variable nothing read, and the chips
     rendered with an invisible dot. Scoped rather than widening the Documents tab's selector,
     which is how that tab already does it. */
  #dash-log-tab .audit-facet .dt-dot{width:9px;height:9px;border-radius:50%;flex:0 0 auto;
    background:var(--c,var(--ink-faint))}
  /* Outcome wears the severity colours the rows and pills already use, so a chip and the
     entries it filters to are the same colour. */
  #dash-log-tab .audit-facet .dt-dot.tone-good{--c:var(--review-approved)}
  #dash-log-tab .audit-facet .dt-dot.tone-warn{--c:var(--review-in-review)}
  #dash-log-tab .audit-facet .dt-dot.tone-bad{--c:var(--status-doc-rejected,#ef4444)}
  /* Who wears the avatar colours from the table, for the same reason: the dot beside "The AI"
     is the blue the AI's own avatar is drawn in three columns to the right. */
  #dash-log-tab .audit-facet .dt-dot.who-user{--c:var(--ink-faint)}
  #dash-log-tab .audit-facet .dt-dot.who-ai{--c:var(--accent)}
  #dash-log-tab .audit-facet .dt-dot.who-system{--c:var(--internal-color)}
  #dash-log-tab .audit-facet .dt-dot.who-api-key{--c:var(--demo-color)}
  /* Nobody signed in: an outline, matching the dashed avatar rather than inventing a colour
     for the absence of a person. */
  #dash-log-tab .audit-facet .dt-dot.who-anonymous{background:transparent;
    border:1.5px dashed var(--line-strong)}

  /* ---- the pager, drawn twice and identical both times ------------------------------- */
  #dash-log-tab .audit-pager{display:flex;flex-wrap:wrap;align-items:center;gap:10px;
    margin:0 0 10px;font-size:12.5px;color:var(--ink-dim)}
  #dash-log-tab #audit-pager-bottom{margin:12px 0 0}
  #dash-log-tab .audit-range{font-variant-numeric:tabular-nums}
  #dash-log-tab .audit-capped{font-size:11.5px;color:var(--review-in-review);
    border:1px solid color-mix(in srgb,var(--review-in-review) 40%,transparent);
    background:color-mix(in srgb,var(--review-in-review) 10%,transparent);
    border-radius:999px;padding:1px 9px}
  #dash-log-tab .audit-size{display:inline-flex;align-items:center;gap:6px;margin-left:auto;
    font-size:12px;color:var(--ink-faint)}
  #dash-log-tab .audit-size .fld{padding:5px 8px;font-size:12.5px;width:auto}
  #dash-log-tab .audit-pages{display:inline-flex;align-items:center;gap:4px;flex-wrap:wrap}
  #dash-log-tab .pgbtn{border:1px solid var(--line);background:var(--panel);color:var(--ink-dim);
    font:inherit;font-size:12.5px;min-width:30px;height:28px;padding:0 8px;border-radius:8px;
    cursor:pointer;font-variant-numeric:tabular-nums}
  #dash-log-tab .pgbtn:hover:not([disabled]){color:var(--ink);border-color:var(--line-strong)}
  #dash-log-tab .pgbtn.on{background:var(--accent);border-color:var(--accent);color:#fff}
  #dash-log-tab .pgbtn[disabled]{opacity:.45;cursor:default}
  #dash-log-tab .audit-gap{color:var(--ink-faint);padding:0 2px}
  #dash-log-tab #audit-export{text-decoration:none}

  /* The expander arrow. `stroke:currentColor` with no fill is how every icon in this app is
     drawn, which is what makes it legible on both grounds — it inherits the button's colour
     rather than carrying one of its own. */
  #dash-log-tab .al-exp .ico{stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;
    stroke-linejoin:round}  #liveverifymodal{position:fixed;inset:0;z-index:73;background:var(--bg);display:none;flex-direction:column}
  #liveverifymodal.show{display:flex}
  .lv-steps{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12.5px;line-height:1.5}
  .lv-step{padding:8px 12px;border-bottom:1px solid var(--line)}
  .lv-step:last-child{border-bottom:none}
  .lv-step .lv-q{color:var(--ink);word-break:break-word;white-space:pre-wrap}
  .lv-step .lv-r{color:var(--ink-dim);margin-top:3px;white-space:pre-wrap;word-break:break-word}
  .lv-step .lv-r.err{color:var(--status-doc-rejected,#d0483c)}
  .lv-n{color:var(--ink-faint);margin-right:8px}
  .lv-shot{display:block;max-width:420px;width:100%;margin-top:8px;border:1px solid var(--line);
    border-radius:8px;cursor:zoom-in}
  .lv-card ol{margin:0;padding-left:22px}
  .lv-card ol li{margin:5px 0}
  .lv-card{background:var(--panel-2);border:1px solid var(--line);border-radius:12px;padding:14px 18px;margin:0 0 14px}
  .lv-card h4{font-family:var(--font-display);font-size:13px;margin:0 0 8px;text-transform:uppercase;
    letter-spacing:.03em;color:var(--ink-faint)}
  .lv-card p,.lv-card li{font-size:13.5px;line-height:1.6;color:var(--ink)}
  .lv-card ul{margin:0;padding-left:20px}
  .lv-conf{display:inline-block;font-size:11.5px;padding:2px 9px;border-radius:999px;
    background:var(--panel);border:1px solid var(--line);color:var(--ink-dim)}
  /* A disarmed target is the resting state, so "armed" is what has to look loud. */
  .arm-btn{white-space:nowrap}
  .arm-btn.armed{background:var(--status-needs-update,#e0762d);border-color:var(--status-needs-update,#e0762d);color:#fff}
  .arm-confirm{display:flex;flex-direction:column;gap:7px;min-width:230px}
  .arm-confirm .arm-warn{font-size:12px;line-height:1.45;color:var(--ink-dim)}
  .arm-confirm .arm-row{display:flex;gap:6px}
  .targets-warn{background:color-mix(in srgb,var(--status-doc-rejected,#d0483c) 12%,transparent);
    border:1px solid color-mix(in srgb,var(--status-doc-rejected,#d0483c) 40%,transparent);
    border-radius:12px;padding:12px 16px;margin:0 0 18px;font-size:13.5px;line-height:1.55;color:var(--ink)}
  .tgt-conn{display:flex;flex-direction:column;gap:5px;min-width:190px}
  .tgt-meta{font-size:11.5px;color:var(--ink-faint)}
  #helpmodal{position:fixed;inset:0;z-index:72;background:var(--bg);display:none;flex-direction:column}
  #helpmodal.show{display:flex}
  .cfg-textarea{min-height:120px;resize:vertical;line-height:1.5}
  .cfg-help{background:var(--panel-2);border:1px solid var(--line);border-radius:12px;padding:6px 20px 16px;margin:0 0 20px;font-size:13.5px;line-height:1.6;color:var(--ink)}
  .cfg-help h3{font-family:var(--font-display);font-size:14px;font-weight:600;margin:16px 0 6px}
  .cfg-help p{margin:0 0 8px;color:var(--ink-dim)}
  .cfg-help ul{margin:0 0 8px;padding-left:20px}
  .cfg-help li{margin:4px 0;color:var(--ink-dim)}
  .cfg-help b{color:var(--ink)}
  #aidoc-foot,#dash-ai-foot{display:flex;align-items:center;gap:12px;padding:12px 22px;border-top:1px solid var(--line);
    background:color-mix(in srgb,var(--panel) 92%,transparent);backdrop-filter:blur(10px)}
  /* The console's footer inside the dashboard tab (V0.44.1): sticky to the bottom of the
     scrolling .perm-wrap, outside the .card so the card's overflow-x cannot become its root. */
  #dash-ai-foot{position:sticky;bottom:0;margin:14px -22px -60px;z-index:2}
  #aidoc-progress{font-size:12.5px;color:var(--ink-dim)}
  .aidoc-nodecell{display:flex;align-items:center;gap:9px}
  .aidoc-nodecell .nd{width:11px;height:11px;border-radius:50%;flex:0 0 auto}
  /* field-sizing:content is the whole auto-grow mechanism (chromium 123+; browsers without it
     keep the min-height + manual resize:vertical, which is exactly what this box always was).
     No JS autosize — one implementation. A manual resize-drag pins the height and content
     sizing stops for that element, which is the right reading of an explicit override. */
  .aidoc-prompt{width:100%;min-height:52px;field-sizing:content;background:var(--panel-2);border:1px solid var(--line);color:var(--ink);
    border-radius:8px;padding:7px 9px;font:inherit;font-size:12.5px;line-height:1.4;resize:vertical;outline:none}
  .aidoc-prompt:focus{border-color:var(--accent)}
  .aidoc-empty{color:var(--ink-faint);font-size:13px;padding:22px 4px}
  .aidoc-claude-badge{display:inline-flex;align-items:center;gap:3px;margin-left:7px;padding:1px 7px;border-radius:999px;
    font-size:10.5px;font-weight:600;color:var(--accent);background:color-mix(in srgb,var(--accent) 15%,transparent);
    border:1px solid color-mix(in srgb,var(--accent) 40%,transparent);white-space:nowrap;vertical-align:middle}
  .aidoc-actions{display:flex;flex-wrap:wrap;gap:6px}
  .aidoc-actbtn{font:inherit;font-size:11.5px;font-weight:600;padding:4px 9px;border-radius:7px;cursor:pointer;
    background:var(--panel-2);border:1px solid var(--line);color:var(--ink);white-space:nowrap;transition:background .12s,border-color .12s}
  .aidoc-actbtn:hover{border-color:var(--accent)}
  .aidoc-actbtn.reject{color:var(--status-doc-rejected)}
  .aidoc-actbtn.accept,.aidoc-actbtn.approve{color:var(--status-approved)}
  .aidoc-actbtn.needs{color:var(--status-needs-update)}
  .aidoc-actbtn.regen{color:var(--status-doc-generated)}
  .aidoc-actbtn.nodoc{color:var(--status-not-started)}
  .aidoc-bulkbar{display:flex;align-items:center;flex-wrap:wrap;gap:8px;padding:8px 2px 12px;font-size:12px;color:var(--ink-dim)}
  .aidoc-bulkbar .aidoc-bulklabel{font-weight:600;color:var(--ink-dim)}
  /* Node cell rendered like a main-mindmap node (its color/format), minus the doc-bean/tag decorations. */
  .aidoc-node{display:inline-flex;align-items:center;gap:8px;max-width:100%;box-sizing:border-box;
    padding:6px 12px;border-radius:10px;border:1px solid;font-size:13px;font-weight:500;line-height:1.3}
  .aidoc-node.root{font-family:var(--font-display);font-weight:600;color:#fff;border:none;padding:7px 14px}
  .aidoc-node .swatch{width:9px;height:9px;border-radius:50%;flex:0 0 auto}
  .aidoc-node .txt{white-space:normal;word-break:break-word;overflow-wrap:anywhere}
  /* The popup's seat (V0.47): the chip at the size a node lands at — between the map's 13.5px
     node and the band's 16px `big` bubble. The heading it replaces was 22px display type beside
     a 12px dot; this is a node, so the node can become it. */
  .aidoc-node.seat{font-size:16px;font-weight:600;padding:12px 18px;border-radius:11px;gap:11px;
    min-width:0;max-width:100%;flex:0 1 auto;line-height:1.35}
  .aidoc-node.seat .swatch{width:11px;height:11px}
  .aidoc-node.seat.root{padding:12px 20px}
  /* Empty seat: the box is drawn, the node has not arrived (or has left). Opacity rather than
     visibility, so nothing beside it — the star, the bell — is ever reported hidden. */
  .aidoc-node.seat.empty > *{opacity:0}

  /* ---- the name and the colour, edited IN the seat (V0.51) ----------------------------
     The popup carried a "Name" line and a "Color" line under a seat already showing both —
     the node twice, once as itself and once as a form. The chip is the form now.
     ONE GRID CELL holds the label and the input, so the span keeps sizing the box while the
     input takes the typing: an independently sized input would make the chip jump the moment
     it appeared, and the flight re-measures its destination from exactly this box. */
  .aidoc-node.seat .seat-name{display:grid;min-width:0;align-items:center}
  .aidoc-node.seat .seat-name > *{grid-area:1/1;min-width:0}
  #popup .aidoc-node.seat #pp-name.seat-in{width:100%;min-width:40px;margin:0;padding:0;border:none;
    background:none;outline:none;font:inherit;color:inherit;line-height:inherit;opacity:0}
  /* AT REST the chip is pixel-identical to V0.47's; on focus the span steps aside and the input
     takes its place, underlined rather than boxed — the chip's own border is already the frame.
     `opacity` rather than `visibility` so the input stays focusable, tabbable and fillable,
     which is also what keeps `#pp-name` a real visible input to the suite. */
  #popup .aidoc-node.seat .seat-name:focus-within .txt{visibility:hidden}
  #popup .aidoc-node.seat .seat-name:focus-within #pp-name.seat-in{opacity:1;border-radius:4px;
    box-shadow:inset 0 -2px 0 var(--accent);background:color-mix(in srgb,var(--panel) 70%,transparent)}
  /* The swatch IS the colour input — `.tagrow input.dot`'s trick at the seat's 11px. */
  #popup .aidoc-node.seat input.swatch{appearance:none;-webkit-appearance:none;width:11px;height:11px;
    padding:0;border:none;border-radius:50%;background:none;cursor:pointer;flex:0 0 auto}
  #popup .aidoc-node.seat input.swatch::-webkit-color-swatch-wrapper{padding:0}
  #popup .aidoc-node.seat input.swatch::-webkit-color-swatch{border:none;border-radius:50%}
  #popup .aidoc-node.seat input.swatch::-moz-color-swatch{border:none;border-radius:50%}
  #popup .aidoc-node.seat input.swatch:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
  /* The ✎ — always drawn, not on hover (the user's choice): an affordance that appears only
     under the pointer is one nobody finds who did not already know it was there. */
  #popup .aidoc-node.seat .pen{width:13px;height:13px;flex:0 0 auto;opacity:.45;fill:none;
    stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
  #popup .aidoc-node.seat:hover .pen{opacity:.8}
  #popup .aidoc-node.seat .seat-name:focus-within ~ .pen{opacity:0}
  /* Documentation link as the blue doc pill (matches the documentation bean color).
     inline-BLOCK, not inline-flex: the pill holds only its title text, and text-overflow never
     renders inside a flex container (the anonymous flex item's min-width:auto stops it
     shrinking, so a long title would hard-clip with no ellipsis in the fixed-layout column). */
  .aidoc-docpill{display:inline-block;max-width:100%;box-sizing:border-box;
    padding:5px 12px;border-radius:999px;font-size:12px;font-weight:600;text-decoration:none;white-space:nowrap;
    overflow:hidden;text-overflow:ellipsis;background:color-mix(in srgb,var(--doc-color) 16%,transparent);
    color:var(--doc-color);border:1px solid color-mix(in srgb,var(--doc-color) 45%,transparent);transition:background .12s}
  .aidoc-docpill:hover{background:color-mix(in srgb,var(--doc-color) 28%,transparent)}
  /* The console's table (#dash-ai, the dashboard's AI tab since V0.44.1): fixed layout so the
     AI-prompt column takes every pixel the fixed-width columns leave, instead of the widest cell
     anywhere deciding — the widths it reads are the <colgroup> in AiTab.tsx, the Documents
     tab's where the columns mean the same thing. The host's `wide` class gives both tabs the
     same width. */
  #dash-ai{table-layout:fixed;min-width:1180px}
  #dash-ai th{white-space:nowrap}
  #dash-ai td{vertical-align:top}
  .rowstate{font-size:11.5px;font-weight:600;white-space:nowrap}
  .rowstate.pending{color:var(--ink-faint)}
  .rowstate.generating{color:var(--accent)}
  .rowstate.done{color:var(--status-approved)}
  .rowstate.error{color:var(--status-doc-rejected)}
  .dash-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px}
  .dash-tile{background:var(--panel);border:1px solid var(--line);border-radius:12px;padding:16px 18px}
  .dash-tile .dash-num{font-family:var(--font-display);font-size:26px;font-weight:600;color:var(--ink)}
  .dash-tile .dash-label{font-size:12.5px;color:var(--ink-dim);margin-top:4px}
  .dash-section{grid-column:1/-1;font-size:13px;font-weight:600;color:var(--ink-dim);text-transform:uppercase;
    letter-spacing:.04em;margin-top:10px;padding-top:10px;border-top:1px solid var(--line)}
  .dash-section:first-child{margin-top:0;padding-top:0;border-top:none}
  .dash-pie-tile{display:flex;flex-direction:column;align-items:center;text-align:center;gap:6px}
  .dash-pie-tile .dash-label{margin-top:0;font-weight:500;color:var(--ink)}
  .dash-pie-sub{font-size:11.5px;color:var(--ink-faint)}
  .dash-section-hint{grid-column:1/-1;font-size:11.5px;color:var(--ink-faint);margin-top:-6px}
  .perm-tab.on .ico{color:#fff}
  .perm-section-lead{font-size:13px;color:var(--ink-dim);margin-bottom:16px}
  .card{background:var(--panel);border:1px solid var(--line);border-radius:14px;padding:16px 18px;margin-bottom:14px}
  .card h3{font-family:var(--font-display);font-size:15px;font-weight:600;margin-bottom:2px;display:flex;align-items:center;gap:10px}
  .card h3 .grow{flex:1}
  .utable{width:100%;border-collapse:collapse;font-size:13.5px}
  .utable th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:var(--ink-faint);
    font-weight:600;padding:6px 10px;border-bottom:1px solid var(--line)}
  .utable td{padding:9px 10px;border-bottom:1px solid var(--line);vertical-align:middle}
  .utable tr:last-child td{border-bottom:none}
  .utable .fld{padding:7px 10px;font-size:13px}
  .u-add-row td{padding-top:12px}
  /* ---- moving a user between groups (V0.40): the grip, the carry, the drop target ---- */
  .utable td.ugrip-cell{width:26px;padding-right:0}
  .ugrip{background:none;border:none;color:var(--ink-faint);cursor:grab;font-size:14px;line-height:1;
    padding:2px 3px;border-radius:6px}
  .ugrip:hover{background:var(--panel-2);color:var(--ink-dim)}
  .ugrip:focus-visible{outline:2px solid var(--accent);outline-offset:1px}
  .ugrip.on{color:var(--accent);background:color-mix(in srgb, var(--accent) 18%, transparent)}
  .urow.carrying{background:color-mix(in srgb, var(--accent) 10%, transparent)}
  .card.dropping{border-color:var(--accent);box-shadow:0 0 0 2px color-mix(in srgb, var(--accent) 35%, transparent)}
  .card h3 .drophere{font-size:11.5px;padding:3px 9px;white-space:nowrap;flex:0 0 auto}
  /* ---- the avatar cell (V0.40): the picture is uploaded here and nowhere else ---- */
  .avcell{display:inline-flex;align-items:center;gap:7px}
  .avcell.busy{opacity:.55}
  .avbtn{background:none;border:none;padding:0;cursor:pointer;display:inline-flex;border-radius:50%}
  .avbtn:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
  .avbtn:disabled{cursor:default}
  /* Smaller and dimmer than the row's delete cross it sits near: one removes a picture, the
     other removes a person, and they must not look like the same button. */
  .avcell .avclear{font-size:13px;opacity:.6}
  .avcell .avclear:hover{opacity:1}
  .perm-grp-name{font-size:16px;font-weight:600;font-family:var(--font-display);background:transparent;border:none;
    border-bottom:1px solid transparent;color:var(--ink);padding:2px 2px;outline:none;min-width:180px}
  .perm-grp-name:focus,.perm-grp-name:hover{border-bottom-color:var(--line-strong)}
  .perm-grp-body{margin-top:12px;display:grid;grid-template-columns:1fr 1fr;gap:18px 26px}
  @media(max-width:680px){.perm-grp-body{grid-template-columns:1fr}}
  .perm-fieldset{min-width:0}
  .perm-fieldset > .fs-lbl{display:block;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.03em;
    color:var(--ink-faint);margin:0 0 9px}
  .chk{display:flex;align-items:flex-start;gap:9px;padding:6px 4px;font-size:13.5px;cursor:pointer;user-select:none;color:var(--ink);border-radius:7px}
  .chk:hover{background:var(--panel-2)}
  .chk input{accent-color:var(--accent);width:16px;height:16px;cursor:pointer;flex:0 0 auto;margin-top:1px}
  .chk .chk-sub{display:block;font-size:11.5px;color:var(--ink-faint);font-weight:400}
  .chk input:disabled{cursor:default}
  .chk.dis{color:var(--ink-faint);cursor:default}
  .pick-box{border:1px solid var(--line);border-radius:10px;background:var(--panel-2);max-height:230px;overflow:auto;padding:5px}
  .pick-row{display:flex;align-items:center;gap:8px;padding:4px 6px;border-radius:6px;font-size:13px;cursor:pointer;user-select:none;white-space:nowrap}
  .pick-row:hover{background:var(--panel)}
  .pick-row input{accent-color:var(--accent);width:15px;height:15px;cursor:pointer;flex:0 0 auto}
  .pick-row .pk-caret{width:14px;flex:0 0 auto;color:var(--ink-faint);text-align:center;font-size:10px;cursor:pointer}
  .pick-row .pk-dot{width:10px;height:10px;border-radius:50%;flex:0 0 auto}
  .pick-row.pk-off{opacity:.5}
  .pick-empty{font-size:12.5px;color:var(--ink-faint);padding:10px 8px}
  .perm-role-lead{grid-column:1/-1}
  .lbtn{border:1px solid var(--line);background:var(--panel);color:var(--ink-dim);font:inherit;font-size:12.5px;
    font-weight:500;padding:7px 12px;border-radius:8px;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
  .lbtn:hover{color:var(--ink);border-color:var(--line-strong)}
  .lbtn.danger:hover{color:#ff7a7a;border-color:#ff7a7a55}
  .icon-x{border:none;background:transparent;color:var(--ink-faint);cursor:pointer;font-size:17px;line-height:1;padding:2px 6px;border-radius:6px}
  .icon-x:hover{color:#ff7a7a;background:var(--panel-2)}

  /* ===== privacy page (RGPD Essentials) ===== */
  /* The same full-screen shape as #helpmodal, but ABOVE the login overlay (z-index 80):
     the page is reachable from the sign-in card, so it has to be able to cover it. */
  #privacymodal{position:fixed;inset:0;z-index:82;background:var(--bg);display:none;flex-direction:column}
  #privacymodal.show{display:flex}
  #privacymodal .card p{font-size:13.5px;color:var(--ink-dim);line-height:1.55;margin-top:8px}
  #privacymodal .card ul{font-size:13.5px;color:var(--ink-dim);line-height:1.55;margin:8px 0 0 18px;padding:0}
  #privacymodal .card li{margin-bottom:6px}
  /* The quiet text link on the sign-in card. Outside the form's tab order (tabindex=-1 in
     the markup) so tabbing through the card stays username -> password -> sign in. */
  .login-privacy{display:block;margin:14px auto 0;border:none;background:none;padding:0;
    font:inherit;font-size:12px;color:var(--ink-faint);cursor:pointer;text-decoration:underline;text-underline-offset:2px}
  .login-privacy:hover{color:var(--ink-dim)}
  /* The Privacy line at the foot of the About panel. */
  #about .ab-privacy{display:flex;align-items:center;gap:7px;width:100%;border:none;background:none;padding:0;
    font:inherit;font-size:12.5px;color:var(--ink-dim);cursor:pointer;text-align:left}
  #about .ab-privacy:hover{color:var(--ink)}
  #about .ab-privacy .ico{width:14px;height:14px}

  /* ===== My items (plan 2.4): favorites + recently viewed, and the star toggles ===== */
  /* The star. Gold when on — a literal, like .row-x's #ff7a7a: one-off accents are not
     design tokens (theme/tokens.test.ts holds the :root/.light blocks to tokens.ts). */
  .favbtn{border:none;background:transparent;color:var(--ink-faint);cursor:pointer;width:28px;height:28px;
    border-radius:8px;display:grid;place-items:center;flex:0 0 auto;padding:0}
  .favbtn:hover{color:var(--ink);background:var(--panel-2)}
  .favbtn.on,.favbtn.on:hover{color:#f5b83d}
  .myitems-title{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;
    color:var(--ink-faint);padding:8px 7px 4px}
  .myitems-title-flex{display:flex;align-items:center;justify-content:space-between;gap:8px}
  /* The "My assignments" eye toggle (V0.42) — reveals my assignments on the map. */
  .myitems-eye{flex:0 0 auto;display:grid;place-items:center;width:22px;height:20px;padding:0;
    border:none;background:transparent;color:var(--ink-faint);cursor:pointer;border-radius:6px}
  .myitems-eye svg{width:15px;height:15px;fill:none;stroke:currentColor;stroke-width:2;
    stroke-linecap:round;stroke-linejoin:round}
  .myitems-eye:hover{background:var(--panel-2);color:var(--ink)}
  .myitems-eye.on{color:#14b8a6}
  .myitems-empty{font-size:12px;color:var(--ink-faint);padding:2px 7px 8px;line-height:1.45}
  /* The assignment row's trailing note — the locale, and "via {team}" (V0.38). */
  .myitems-via{flex:0 0 auto;font-size:11px;color:var(--ink-faint);white-space:nowrap}
  /* One list row (components/ItemRow.tsx) — .tagrow's look, so the sidebar's two list
     families read as siblings. Phase 3's feed panel reuses the same component and classes. */
  .itemrow{display:flex;align-items:center;gap:7px;padding:5px 7px;border-radius:8px;cursor:pointer;
    font-size:13px;user-select:none}
  .itemrow:hover{background:var(--panel-2)}
  .itemrow .dot{width:11px;height:11px;border-radius:50%;flex:0 0 auto;border:1px solid rgba(0,0,0,.15)}
  .itemrow > .ico{width:14px;height:14px;flex:0 0 auto;color:var(--ink-dim)}
  .itemrow .name{flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .itemrow .row-x{opacity:0;color:var(--ink-faint);cursor:pointer;font-size:15px;line-height:1;padding:0 2px}
  .itemrow:hover .row-x{opacity:.6}
  .itemrow .row-x:hover{opacity:1;color:#ff7a7a}
  /* The per-row LOCATE eye (V0.42.1) — same hover-reveal manners as .row-x beside it, in the
     assignment teal rather than the delete red: it navigates, it does not remove anything. */
  .itemrow .row-locate{opacity:0;color:var(--ink-faint);cursor:pointer;line-height:1;padding:0 2px;
    display:grid;place-items:center;flex:0 0 auto}
  .itemrow .row-locate svg{width:14px;height:14px;fill:none;stroke:currentColor;stroke-width:2;
    stroke-linecap:round;stroke-linejoin:round}
  .itemrow:hover .row-locate{opacity:.6}
  .itemrow .row-locate:hover{opacity:1;color:#14b8a6}

  /* ---- the tags dropdown and its dot button (plan 2.3) ---- */
  /* The dot button: .sfilter-btn's box, its face a row of dots — one per selected tag, in
     that tag's colour. flex-wrap lets 3 dots + "+N" break onto a second row inside 32px. */
  .tagdot-btn{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:2px;padding:2px 4px}
  .tagdot{width:7px;height:7px;border-radius:50%;flex:0 0 auto;border:1px solid rgba(0,0,0,.15)}
  .tagdot.neutral{width:9px;height:9px;background:transparent;border:1.5px solid var(--ink-dim)}
  .tagdot-more{font-size:8px;font-weight:700;line-height:1;color:var(--ink-dim)}
  .sfilter-btn.on .tagdot.neutral{border-color:var(--accent)}
  /* The panel: .filterpanel's family (same anchor, surface and z-plane), but a flex column
     so the tree scrolls in its .side-body ALONE — the panel itself never clips, or the
     drag-and-drop drop bands would vanish under the scrollbar. */
  .tagpanel{position:absolute;top:calc(100% + 8px);right:0;z-index:35;width:300px;max-height:60vh;
    display:none;flex-direction:column;background:var(--panel);border:1px solid var(--line);
    border-radius:12px;box-shadow:0 10px 30px rgba(0,0,0,.28);overflow:hidden}
  .tagpanel.show{display:flex}
  .tagpanel .side-head{padding:10px 12px 8px}
  /* min-height:0 lets the body actually shrink inside the max-height column (flex items
     default to min-height:auto, which would push the footer out instead of scrolling). */
  .tagpanel .side-body{min-height:0}
  .tagpanel .side-head .icobtn{width:26px;height:26px}
  .tagpanel .icobtn.on{border-color:var(--accent);color:var(--accent)}
  #tag-panel-close{font-size:16px;line-height:1}

  /* ---- search scope panel additions + doc-content results dropdown (plan 2.2) ---- */
  /* A selected document type takes the text scopes out of play: greyed, not hidden — the
     narrowing must stay visible, and pointer-events off so the refused click is obvious. */
  .filterrow.disabled{opacity:.45;pointer-events:none}
  .filtergroup{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;
    color:var(--ink-faint);padding:9px 9px 4px;border-top:1px solid var(--line);margin-top:4px}
  .filterhint{font-size:11.5px;color:var(--ink-faint);padding:4px 9px 7px;line-height:1.4}
  /* The results dropdown: .filterpanel's family (surface, border, z-plane), anchored under
     the search box's left edge and wide enough for a snippet to mean something. */
  .searchresults{position:absolute;top:calc(100% + 8px);left:0;z-index:35;width:380px;max-width:60vw;
    max-height:50vh;overflow:auto;background:var(--panel);border:1px solid var(--line);
    border-radius:12px;box-shadow:0 10px 30px rgba(0,0,0,.28);padding:6px;display:none}
  .searchresults.show{display:block}
  .sr-status{font-size:12.5px;color:var(--ink-faint);padding:8px 9px}
  .sr-row{display:flex;align-items:flex-start;gap:9px;padding:7px 9px;border-radius:8px;cursor:pointer}
  .sr-row:hover{background:var(--panel-2)}
  .sr-row > .ico{width:15px;height:15px;flex:0 0 auto;margin-top:2px;color:var(--ink-dim)}
  .sr-main{flex:1;min-width:0}
  .sr-title{font-size:13px;color:var(--ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .sr-title b{color:var(--accent);font-weight:700}
  .sr-node{font-size:11.5px;color:var(--ink-faint);margin-left:7px}
  .sr-snip{font-size:12px;color:var(--ink-dim);line-height:1.45;margin-top:2px;
    display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .sr-snip b{color:var(--ink);font-weight:700}
  .sr-more{font-size:11.5px;color:var(--ink-faint);padding:7px 9px 5px;border-top:1px solid var(--line);margin-top:4px}
  /* Node results (V0.46) wear the map's own design — the .node rule's radius, padding, gap
     and swatch, with the tint and border colour supplied inline from the node's colour
     exactly as styleFor() does. A result should look like the thing it takes you to. */
  .sr-noderow{display:flex;align-items:center;gap:9px;box-sizing:border-box;padding:7px 11px;
    border-radius:11px;border:1px solid;background:var(--panel);color:var(--ink);
    font-size:13.5px;font-weight:500;line-height:1.35;cursor:pointer;margin:0 0 4px}
  .sr-noderow:last-of-type{margin-bottom:0}
  .sr-noderow:hover{filter:brightness(1.18)}
  .sr-noderow .swatch{width:9px;height:9px;border-radius:50%;flex:0 0 auto}
  .sr-nodename{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .sr-nodename b{color:var(--accent);font-weight:700}
  .sr-sep{height:1px;background:var(--line);margin:7px 2px}
  /* A document row carries TWO colours: its node's down the left edge and beside the node
     name, and its kind's in the pill on the right. */
  .sr-row{border-left:3px solid transparent;padding-left:8px}
  .sr-nodedot{display:inline-block;width:6px;height:6px;border-radius:50%;margin-right:5px;
    vertical-align:middle;flex:0 0 auto}
  .sr-kind{flex:0 0 auto;align-self:center;font-size:10.5px;font-weight:600;letter-spacing:.02em;
    padding:2px 7px;border-radius:999px;border:1px solid;white-space:nowrap}

  /* ---- topbar overflow (plan 1.3) ---- */
  /* The 720px media rule further up hides .search-count and .user-chip — but the .user-chip
     BASE rule sits later in the file, re-sets display, and silently won on source order, so
     the chip never actually hid. The overflow controller's width budget needs the stated
     behaviour to be true (it is what keeps the search alive on narrow edit-mode bars), so
     the rule is restated here, after the base rule it lost to. */
  @media(max-width:720px){.user-chip{display:none}}

  /* ---- route notice (plan 1.6): the unreachable-link banner ---- */
  /* The toast family's slot (see .saved), one row lower so a save landing at the same moment
     does not sit on top of it. Persistent until dismissed rather than fading: "why did my
     link not work" is read after the map has finished drawing itself. */
  #routenotice{display:none}
  #routenotice.show{display:block}
  /* max-width + line-height added in V0.34: the concurrency wordings are ~120 characters
     against the deep-link notices' ~35, and a 120-character single line would run off both
     edges of a narrow window. No pixel baseline photographs this banner. */
  /* 81, ABOVE the login card (#loginmodal is 80 and opaque) and below the privacy notice
     (82). The 'session-ended' wording exists to be read while that card is up, and at 40 it
     was painted underneath it — a notice nobody could see. */
  .route-note{position:fixed;top:104px;left:50%;transform:translateX(-50%);z-index:81;
    display:flex;align-items:center;gap:10px;background:var(--panel);border:1px solid var(--line);
    color:var(--ink);font-size:12.5px;font-weight:500;padding:7px 10px 7px 14px;border-radius:18px;
    box-shadow:0 10px 26px rgba(0,0,0,.30);
    max-width:min(560px,calc(100vw - 32px));line-height:1.35}
  .route-note-detail{color:var(--ink-dim)}
  .route-note-x{background:none;border:none;cursor:pointer;color:var(--ink-dim);
    font-size:15px;line-height:1;padding:2px 4px;border-radius:6px}
  .route-note-x:hover{color:var(--ink);background:var(--panel-2)}

  /* ---- re-slug: the button's mark, and its dialog (V0.45.1) ---- */
  /* .favbtn's box exactly, tinted because this one MEANS something rather than toggling a
     preference. `position:relative` is for the badge; .favbtn does not set it. */
  .reslugbtn{position:relative;color:var(--accent)}
  .reslugbtn:hover{color:var(--accent);background:color-mix(in srgb,var(--accent) 14%,transparent)}
  /* The "!": drawn only while the address has stopped following the name. The panel-coloured
     ring is what lets it sit over the glyph without reading as part of it. */
  .reslug-badge{position:absolute;top:0;right:0;width:13px;height:13px;border-radius:50%;
    background:#e0342a;color:#fff;border:1.5px solid var(--panel);
    font-size:9px;font-weight:700;line-height:1;display:grid;place-items:center;padding-bottom:.5px}
  /* 76: above the doc overlay's stack (70-74) and the node popup (46), below the modal SCREENS
     at 80 (login, doc picker, log detail). A dialog over a working surface, not a screen. */
  .reslug-scrim{position:fixed;inset:0;z-index:76;background:rgba(8,11,16,.62);
    display:flex;align-items:flex-start;justify-content:center;padding:6vh 20px;overflow:auto}
  .reslug-dlg{background:var(--panel);color:var(--ink);border:1px solid var(--line);
    border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.5);width:100%;max-width:520px;
    padding:24px 26px 22px}
  .reslug-dlg h4{font-family:var(--font-display);font-size:18px;font-weight:600;
    display:flex;align-items:center;gap:9px}
  .reslug-dlg h4 .ico{width:18px;height:18px;color:var(--accent)}
  .reslug-subject{font-size:13px;color:var(--ink-dim);margin-top:3px}
  .reslug-lab{display:block;font-size:11.5px;font-weight:600;letter-spacing:.04em;
    text-transform:uppercase;color:var(--ink-faint);margin:19px 0 7px}
  .reslug-url{font-family:ui-monospace,Consolas,monospace;font-size:13px;
    background:var(--panel-2);border:1px solid var(--line);border-radius:8px;padding:8px 10px;
    word-break:break-all}
  .reslug-url .pre{color:var(--ink-faint)}
  .reslug-url .tail{color:var(--ink);font-weight:500}
  .reslug-url.was .tail{color:var(--ink-faint);text-decoration:line-through}
  .reslug-dlg .fld{font-family:ui-monospace,Consolas,monospace;width:100%}
  .reslug-note{font-size:12.5px;color:var(--ink-dim);margin-top:7px}
  .reslug-note.warn{color:var(--status-needs-update)}
  .reslug-release{display:flex;gap:10px;align-items:flex-start;margin-top:18px;
    font-size:13px;color:var(--ink-dim);cursor:pointer}
  .reslug-release input{margin-top:3px;accent-color:var(--accent);flex:0 0 auto}
  .reslug-release b{color:var(--ink);font-weight:600}
  .reslug-conseq{margin-top:15px;border-radius:9px;padding:11px 13px;font-size:13px;border:1px solid}
  .reslug-conseq.keep{color:var(--status-approved);
    background:color-mix(in srgb,var(--status-approved) 12%,transparent);
    border-color:color-mix(in srgb,var(--status-approved) 35%,transparent)}
  .reslug-conseq.brk{color:var(--status-doc-rejected);
    background:color-mix(in srgb,var(--status-doc-rejected) 12%,transparent);
    border-color:color-mix(in srgb,var(--status-doc-rejected) 35%,transparent)}
  .reslug-foot{display:flex;justify-content:flex-end;gap:9px;margin-top:20px;flex-wrap:wrap}

  /* ---- the documentation workflow (V0.38; Phase 4a's review control, unified) ---- */
  /* The doc bar's state chip. .favbtn's neighbour in the same row, but a chip rather than an
     icon button: "Draft" and "In review" are words, and a third silent glyph beside the star
     and the bell would say nothing a reader could decode. Renamed .reviewchip -> .workflowchip
     with the V0.38 unification — SAME geometry and colours, so the photographed doc bar is
     pixel-identical. */
  .workflowchip{display:inline-flex;align-items:center;gap:7px;flex:0 0 auto;cursor:pointer;
    background:var(--panel-2);border:1px solid var(--line);color:var(--ink-dim);
    border-radius:999px;padding:5px 12px 5px 10px;font:inherit;font-size:12px;font-weight:600;
    white-space:nowrap;transition:border-color .15s,color .15s}
  .workflowchip:hover{border-color:var(--line-strong);color:var(--ink)}
  .reviewdot{width:8px;height:8px;border-radius:50%;flex:0 0 auto;display:inline-block}
  /* The doc bar's OWNERSHIP CLUSTER (V0.42, Option A — the validated POC): language ·
     responsibles · workflow state, boxed together as ONE workflow unit after the bell. The
     border and the faint WORKFLOW tag set the three apart from the personal star and bell. */
  .wf-cluster{display:inline-flex;align-items:center;gap:7px;flex:0 1 auto;min-width:0;position:relative;margin-left:2px;
    background:color-mix(in srgb,var(--accent) 5%,var(--panel));
    border:1px solid color-mix(in srgb,var(--accent) 30%,var(--line));border-radius:11px;padding:4px 7px}
  .wf-cluster-tag{position:absolute;top:-8px;left:9px;padding:0 5px;line-height:1;border-radius:3px;
    font-family:ui-monospace,SFMono-Regular,monospace;font-size:8.5px;letter-spacing:.14em;
    color:var(--ink-faint);background:var(--panel)}
  .wf-cluster .wf-div{width:1px;align-self:stretch;margin:3px 0;opacity:.7;
    background:color-mix(in srgb,var(--accent) 26%,var(--line))}
  .wf-lang{border:1px solid var(--line);background:var(--panel);border-radius:8px;padding:4px 6px;
    font:inherit;font-size:12px;font-weight:600;color:var(--ink);cursor:pointer}
  .wf-lang.static{color:var(--ink-dim);cursor:default}
  .wf-respbox{display:inline-flex;align-items:center;gap:6px;flex:0 1 auto;min-width:0;max-width:230px;
    background:var(--panel);border:1px solid var(--line);border-radius:8px;padding:3px 8px 3px 5px;
    cursor:pointer;font:inherit;overflow:hidden}
  .wf-respbox:hover{border-color:var(--line-strong)}
  .wf-resp-stack{display:inline-flex;flex:0 0 auto}
  .wf-resp-av{width:20px;height:20px;border-radius:50%;display:grid;place-items:center;flex:0 0 auto;
    font-size:8.5px;font-weight:700;color:#fff;background:var(--accent)}
  .wf-resp-av.grp{border-radius:6px;background:color-mix(in srgb,#a06bff 65%,transparent)}
  .wf-resp-stack .wf-resp-av + .wf-resp-av{margin-left:-7px;border:1.5px solid var(--panel)}
  .wf-resp-count{font-size:12px;color:var(--ink-dim);white-space:nowrap}
  .wf-resp-none{font-size:12px;color:var(--ink-faint);white-space:nowrap}
  .wf-caret{color:var(--ink-faint);font-size:10px;flex:0 0 auto}
  /* The responsibles dropdown's contents. A generous MIN-HEIGHT so the SubjectPicker's own
     option menu (absolutely positioned, `.subj-menu`) fits ENTIRELY inside the popover paper —
     so the whole dropdown never scrolls, only the inner users/groups list does (V0.42). */
  .wf-resp-panel{width:320px;max-width:80vw;padding:10px 12px 14px;min-height:410px;overflow:visible}
  .wf-resp-panel .subj-list{max-height:250px}
  /* The panel body. MUI's Popover paper carries the surface (theme.ts); this is its contents,
     sized like the feed panel next door. */
  .workflowpanel{width:330px;max-width:74vw;padding:10px 12px 12px}
  /* The popover's doc-responsibles row (V0.38): breathing room around the shared picker. */
  .wf-assign{margin:2px 0 9px}
  .review-head{display:flex;align-items:baseline;justify-content:space-between;gap:8px;margin-bottom:8px}
  .review-title{font-size:13px;font-weight:700;color:var(--ink)}
  .review-lang{font-size:11px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:var(--ink-faint)}
  /* The fallback notice. Amber-tinted like .doclogs-stale, because it says the same KIND of
     thing: what you are reading is not what you asked for. */
  .review-fallback{font-size:12px;line-height:1.45;color:var(--ink);border-radius:8px;padding:7px 9px;
    margin-bottom:8px;background:color-mix(in srgb,var(--review-in-review) 14%,transparent);
    border:1px solid color-mix(in srgb,var(--review-in-review) 40%,transparent)}
  .review-meta{font-size:12px;color:var(--ink-dim);margin-bottom:9px}
  .review-actions{display:flex;flex-wrap:wrap;gap:8px}
  .review-reject{margin-top:9px;display:flex;flex-direction:column;gap:8px}
  .review-note{width:100%;min-height:60px;resize:vertical;background:var(--panel-2);
    border:1px solid var(--line);color:var(--ink);border-radius:8px;padding:8px 10px;
    font:inherit;font-size:13px;outline:none}
  .review-note:focus{border-color:var(--accent)}
  .review-sect{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;
    color:var(--ink-faint);padding:10px 0 5px;border-top:1px solid var(--line);margin-top:11px}
  .review-empty{font-size:12.5px;color:var(--ink-faint);padding:2px 0 2px}
  .review-events{list-style:none;display:flex;flex-direction:column;gap:7px}
  .review-events li{display:flex;align-items:flex-start;gap:7px;font-size:12px;color:var(--ink-dim)}
  .review-events .reviewdot{margin-top:4px}
  .review-ev-text{flex:1;min-width:0;line-height:1.4}
  .review-ev-note{display:block;color:var(--ink-faint);font-style:italic}
  .review-ev-when{flex:0 0 auto;color:var(--ink-faint);font-size:11px;white-space:nowrap}
  /* The dot on a node-popup pill: an indicator, never a control — the doc bar's chip is the
     only place a transition happens. The wrapper is always rendered (see PopupDocs) so the
     pill's box never depends on whether a page has a review state; flex, because .docpill is
     inline-flex and a block wrapper would give it a line box it does not have today. */
  .docpill-review{position:relative;display:flex;min-width:0}
  .docpill-review.has-dot > .docpill{padding-right:26px}
  .docpill-reviewdot{position:absolute;right:9px;top:50%;transform:translateY(-50%);
    width:8px;height:8px;border-radius:50%;pointer-events:auto;
    box-shadow:0 0 0 2px color-mix(in srgb,#000 32%,transparent)}
  /* The AI-doc console's read-only Review column (see the colgroup note in AiDocConsole). */
  /* .aidoc-review was the console's read-only Review cell; the column dissolved into the
     seven stage tabs with V0.38's unification and nothing renders the class any more. */

  /* ---- presence avatars navigate (plan 1.7) ---- */
  .presence-stack .avatar{cursor:pointer}

  /* ---- the bell: what-changed feed + watch toggles (plan Phase 3) ---- */
  /* The watch bell is .favbtn's sibling everywhere the star goes; its ON state is accent
     blue so the pair's two lit states (gold star, blue bell) stay tellable at a glance. */
  .favbtn.watch.on,.favbtn.watch.on:hover{color:var(--accent)}
  /* The panel body: MUI's Popover paper carries the surface (theme.ts); these are its
     contents, sized like the search-results dropdown next door. */
  .feedpanel{width:360px;max-width:70vw;max-height:60vh;overflow-y:auto;padding:6px}
  .feed-head{display:flex;align-items:center;justify-content:space-between;padding:5px 9px 6px}
  .feed-title{font-size:13px;font-weight:700;color:var(--ink)}
  .feed-markread{background:none;border:none;cursor:pointer;font-size:11.5px;color:var(--ink-dim);
    padding:3px 6px;border-radius:6px}
  .feed-markread:hover{color:var(--ink);background:var(--panel-2)}
  /* Section headers, .filtergroup's family; day headers one step quieter. */
  .feed-sect{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;
    color:var(--ink-faint);padding:9px 9px 4px;border-top:1px solid var(--line);margin-top:4px}
  .feed-day{font-size:11px;color:var(--ink-faint);padding:6px 9px 2px}
  .feed-status,.feed-empty{font-size:12.5px;color:var(--ink-faint);padding:10px 9px}
  .feedrow .name{font-size:13px}
  .feedrow.unread .name{font-weight:700;color:var(--ink)}
  /* A node's name inside a row's sentence, in the node's own colour and without quote marks
     (V0.45.2). The colour is inline, per node, the way NodeChip does it; only the WEIGHT is
     left alone, because in this panel bold already means unread and one signal per property
     is the rule. `color` here beats .feedrow.unread .name's, which is set on the ancestor. */
  .feed-nodename{color:var(--ink)}
  .feed-meta{font-size:11px;color:var(--ink-faint);white-space:nowrap;flex:0 0 auto}
  .feed-more{font-size:11.5px;color:var(--ink-dim);padding:7px 9px;cursor:pointer;border-radius:8px}
  .feed-more:hover{background:var(--panel-2);color:var(--ink)}

  /* ---- Roles & Permissions as master–detail (V0.41) ---- */
  /* Every role on the left, ONE role's permissions on the right. The list is a fixed column
     and the detail pane carries min-width:0, or .perm-grp-body's two columns would refuse to
     shrink and push the pair wider than the 980px .perm-inner allows — which is horizontal
     scroll, on the one screen that has to work in a half-width window. Below 820px of pane
     the two stack instead, list above detail. */
  .role-split{display:flex;align-items:flex-start;gap:18px}
  .role-list{flex:0 0 240px;width:240px;min-width:0;display:flex;flex-direction:column;gap:4px}
  .role-detail{flex:1 1 auto;min-width:0}
  /* Wider on the permissions page (V0.54.5), which has the room since it took the wide measure:
     at 240px a long role, scope or backup name was cut off in the one place it is picked from. */
  #permmodal .role-list{flex-basis:360px;width:360px}
  @media(max-width:820px){
    .role-split{flex-direction:column}
    .role-list{flex:0 0 auto;width:100%}
    #permmodal .role-list{flex-basis:auto;width:100%}
    .role-detail{width:100%}
  }
  .role-add{justify-content:center;margin-bottom:8px}
  /* The min-height is what keeps the list from twitching when the pen swaps the name for an
     input: the row is the same 36px either way, so the rows below it do not move. */
  .rolerow{display:flex;align-items:center;gap:2px;min-height:36px;padding-right:4px;border-radius:9px;
    border:1px solid transparent;background:var(--panel-2)}
  .rolerow:hover{border-color:var(--line)}
  /* The selected row is the pane's title as much as the heading is, so it is marked with the
     accent rather than merely a darker fill — at a glance the list says which one is open. */
  .rolerow.sel{border-color:var(--accent);background:color-mix(in srgb, var(--accent) 15%, transparent)}
  /* ---- re-ordering the roles (V0.48.1): the grip, and the row while it travels ---- */
  /* The Users table's grip, at the size this row wants. Not a button here — the keyboard
     path is Alt+Up / Alt+Down on the row, so a focusable grip would be a tab stop that did
     nothing. `cursor:grab` over the whole row, because the whole row is what drags. */
  .rolerow{cursor:grab}
  .rolerow:active{cursor:grabbing}
  .rolegrip{flex:0 0 auto;color:var(--ink-faint);font-size:13px;line-height:1;
    padding:0 1px 0 6px;user-select:none}
  .rolerow:hover .rolegrip{color:var(--ink-dim)}
  .rolerow.carrying{opacity:.55}
  /* The left inset is the GRIP's since V0.48.1 (it was 11px here, and the two together were
     an indent nobody asked for), so the name still starts about where it always did. */
  .rolerow-name{flex:1 1 auto;min-width:0;border:none;background:none;color:var(--ink);font:inherit;
    font-size:13.5px;text-align:left;padding:9px 4px 9px 4px;cursor:pointer;border-radius:9px;
    overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .rolerow.sel .rolerow-name{font-weight:600}
  .rolerow-edit{flex:1 1 auto;min-width:0;height:26px;margin:0 0 0 6px;padding:0 9px;font-size:13px}
  .role-pen,.role-bin{flex:0 0 auto;display:inline-flex;border:none;background:transparent;
    color:var(--ink-faint);cursor:pointer;padding:5px;border-radius:6px}
  .role-pen .ico,.role-bin .ico{width:15px;height:15px}
  .role-pen:hover{color:var(--ink);background:var(--panel)}
  .role-bin:hover{color:#ff7a7a;background:var(--panel)}
  .rolerow-name:focus-visible,.role-pen:focus-visible,.role-bin:focus-visible{
    outline:2px solid var(--accent);outline-offset:-2px}
  .role-title{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .role-foot{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .role-foot-note{font-size:11.5px;color:var(--ink-faint)}

  /* ---- dashboard: the Documents tab (V0.44) ---- */
  /* Appended, and only appended: the token blocks at the top of this file are parsed by
     `theme/tokens.test.ts`, and everything below reuses the properties they already define —
     no new token, no edited rule. Two vocabularies live here: the TABLE (scoped to
     `#dash-docs-tab`, which nothing else renders into) and the locale POPOVER, which MUI
     portals to the body, so `.dt-pop` is reachable on its own. The few names they share —
     the state dot, the state chip — are selected for both rather than written twice. */

  /* The widest read surface in the app after the logs, so the host widens while this tab
     shows (Dashboard.tsx toggles `wide`) and the CARD scrolls sideways. The page never does:
     a horizontally scrolling modal loses its own header. */
  #dashboardmodal.wide .perm-inner{max-width:min(1400px,92vw)}
  @media(max-width:1100px){#dashboardmodal.wide .perm-inner{max-width:94vw}}
  #dashboardmodal.wide .card{overflow-x:auto}
  #dash-docs{table-layout:fixed;min-width:1180px}
  #dash-docs th{white-space:nowrap}
  #dash-docs td{vertical-align:top}
  .dt-th-ai{color:var(--accent)}
  /* Owed rows are a different KIND of row, not a worse one: a wash, not a warning. */
  #dash-docs tr.dt-planned td{background:color-mix(in srgb,var(--status-prompt-present) 6%,transparent)}

  /* ---- the summary strip ---- */
  #dash-docs-strip{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px}
  .dt-strip-chip{border:1px solid var(--line);background:var(--panel);color:var(--ink-dim);font:inherit;
    font-size:12.5px;border-radius:999px;padding:5px 11px 5px 8px;display:inline-flex;align-items:center;
    gap:7px;cursor:pointer}
  .dt-strip-chip:hover{color:var(--ink);border-color:var(--line-strong)}
  .dt-strip-chip.on{border-color:var(--accent);color:var(--ink);
    background:color-mix(in srgb,var(--accent) 12%,transparent)}
  .dt-strip-chip b{color:var(--ink);font-variant-numeric:tabular-nums}
  .dt-strip-chip:focus-visible{outline:2px solid var(--accent);outline-offset:1px}

  /* ---- the dot every state is drawn with: the strip, the cells and the popover ---- */
  #dash-docs-tab .dt-dot,.dt-pop .dt-dot{width:9px;height:9px;border-radius:50%;flex:0 0 auto;
    background:var(--c,var(--ink-faint))}
  #dash-docs-tab .dt-dot.dash,.dt-pop .dt-dot.dash{background:transparent;
    border:1.5px dashed var(--c,var(--ink-faint))}

  /* ---- the controls row ---- */
  #dash-docs-tab .logs-controls .filterrow{padding:0 6px;gap:8px}
  #dash-docs-tab .logs-controls .filterrow.ai{color:var(--accent)}
  #dash-docs-tab .iconbtn{border:1px solid var(--line);background:var(--panel);color:var(--ink-dim);
    border-radius:8px;width:28px;height:28px;padding:0;display:inline-grid;place-items:center;cursor:pointer}
  #dash-docs-tab .iconbtn:hover{color:var(--ink);border-color:var(--line-strong)}
  #dash-docs-tab .iconbtn:focus-visible{outline:2px solid var(--accent);outline-offset:1px}
  #dash-docs-tab .iconbtn .ico{width:14px;height:14px}
  /* The dashboard header's language picker says "I moved" with an accent ring that settles
     back — the tables re-read in place, so without it a language switch can look like nothing
     happened. (It sat on the Documents tab's own picker in V0.44; the header owns it now.) */
  @keyframes dt-lang-pulse{
    0%{box-shadow:0 0 0 0 color-mix(in srgb,var(--accent) 70%,transparent);border-color:var(--accent)}
    100%{box-shadow:0 0 0 9px transparent;border-color:var(--line)}
  }
  #dash-lang.pulse{animation:dt-lang-pulse .65s ease-out}
  @media (prefers-reduced-motion: reduce){#dash-lang.pulse{animation:none;border-color:var(--accent)}}
  /* A row the other tab handed over lights up once, so the eye lands on it. */
  @keyframes dt-row-flash{0%{background:color-mix(in srgb,var(--accent) 22%,transparent)}100%{background:transparent}}
  #dash-docs tr.flash td,#dash-ai tr.flash td{animation:dt-row-flash 1.4s ease-out}
  @media (prefers-reduced-motion: reduce){#dash-docs tr.flash td,#dash-ai tr.flash td{animation:none;
    background:color-mix(in srgb,var(--accent) 12%,transparent)}}

  /* ---- Document: the app's own pill (components/DocPill.tsx, nav variant) since V0.44.1 ---- */
  #dash-docs-tab .docnav-pill{max-width:100%}
  /* An owed pill is dashed in its kind's colour (DocPill `planned`) and, for a viewer who may
     create the page, IS the way to create it; for anyone else it is a label. */
  #dash-docs-tab .docnav-pill.planned.static{cursor:default;opacity:.7}
  #dash-docs-tab .docnav-pill.planned.static:hover{background:transparent}
  #dash-docs-tab .aidoc-claude-badge{margin-left:6px}
  #dash-docs-tab .kind{display:block;font-size:11px;color:var(--ink-faint);margin-top:4px;padding-left:2px;
    overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

  /* ---- Node ---- */
  #dash-docs-tab .dt-owner + .dt-owner{margin-top:6px}
  #dash-docs-tab .dt-owner .aidoc-node{font-size:12.5px;padding:5px 11px}
  #dash-docs-tab .dt-crumb{display:block;font-size:11px;color:var(--ink-faint);margin-top:4px;padding-left:2px;
    white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  #dash-docs-tab .dt-muted{font-size:12.5px;color:var(--ink-dim)}

  /* ---- State ---- */
  #dash-docs-tab .dt-state,.dt-pop .dt-state{display:inline-flex;align-items:center;gap:7px;font-size:12.5px;
    font-weight:600;color:var(--ink)}
  #dash-docs-tab .dt-state.muted,.dt-pop .dt-state.muted{color:var(--ink-dim);font-weight:500}
  #dash-docs-tab .dt-state small,.dt-pop .dt-state small{font-weight:500;color:var(--ink-faint);font-size:11px}

  /* ---- Languages ---- */
  #dash-docs-tab .dt-lchips{display:flex;gap:8px;flex-wrap:wrap}
  #dash-docs-tab .dt-lc{--c:var(--ink-faint);position:relative;min-width:34px;height:22px;border-radius:999px;
    border:1.5px solid var(--c);background:color-mix(in srgb,var(--c) 22%,transparent);color:var(--ink);
    font:inherit;font-size:10.5px;font-weight:700;letter-spacing:.04em;display:inline-grid;place-items:center;
    padding:0 6px;cursor:pointer}
  #dash-docs-tab button.dt-lc:hover{filter:brightness(1.14)}
  #dash-docs-tab .dt-lc.none{background:transparent;border-style:dashed;color:var(--ink-faint)}
  #dash-docs-tab span.dt-lc{cursor:default}
  /* Behind the source language: an amber ring OUTSIDE the chip, so the state colour inside
     still reads. */
  #dash-docs-tab .dt-lc.stale::after{content:"";position:absolute;inset:-3px;border-radius:999px;
    border:1.5px solid var(--review-in-review)}
  #dash-docs-tab .dt-lc.cur{box-shadow:0 0 0 2px var(--panel),0 0 0 3.5px var(--ink-dim)}

  /* ---- Responsible ---- */
  #dash-docs-tab .dt-resp{display:inline-flex;align-items:center;gap:7px;max-width:100%;
    border:1px solid var(--line);background:var(--panel);border-radius:8px;padding:3px 8px 3px 5px;
    font:inherit;cursor:pointer}
  #dash-docs-tab .dt-resp:hover{border-color:var(--line-strong)}
  #dash-docs-tab .dt-resp.static{cursor:default;border-color:transparent;background:none;padding-left:0}
  #dash-docs-tab .dt-resp .wf-resp-av{width:20px;height:20px;font-size:8.5px}
  #dash-docs-tab .dt-resp .n{font-size:12px;color:var(--ink-dim);white-space:nowrap;overflow:hidden;
    text-overflow:ellipsis}
  #dash-docs-tab .dt-resp.none .n{color:var(--ink-faint)}
  #dash-docs-tab .dt-resp.inherit .wf-resp-av{background:var(--ink-faint)}
  #dash-docs-tab .dt-caret{color:var(--ink-faint);font-size:10px;flex:0 0 auto}

  /* ---- Last change ---- */
  #dash-docs-tab .dt-when{white-space:nowrap;font-variant-numeric:tabular-nums}
  #dash-docs-tab .dt-kdot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:6px;
    vertical-align:middle;background:var(--c,var(--ink-faint))}
  #dash-docs-tab .who{display:block;font-size:11.5px;color:var(--ink-faint);white-space:nowrap;
    overflow:hidden;text-overflow:ellipsis}

  /* ---- AI ---- */
  #dash-docs-tab .dt-aicell{font-size:12px;color:var(--ink-dim);display:grid;gap:5px;justify-items:start}
  #dash-docs-tab .dt-badge{display:inline-flex;align-items:center;gap:5px;padding:1px 8px;border-radius:999px;
    font-size:11px;font-weight:600;border:1px solid var(--line);color:var(--ink-dim);white-space:nowrap}
  #dash-docs-tab .dt-badge.warn{color:var(--review-in-review);
    border-color:color-mix(in srgb,var(--review-in-review) 50%,transparent);
    background:color-mix(in srgb,var(--review-in-review) 10%,transparent)}
  #dash-docs-tab .dt-badge.ok{color:var(--review-approved);
    border-color:color-mix(in srgb,var(--review-approved) 45%,transparent)}
  #dash-docs-tab .dt-emb{display:inline-flex;gap:4px;align-items:center}
  #dash-docs-tab .dt-emb > span{font-size:10px;font-weight:700;padding:1px 5px;border-radius:5px;
    border:1px solid var(--line);color:var(--ink-faint)}
  #dash-docs-tab .dt-emb > span.on{color:var(--review-approved);
    border-color:color-mix(in srgb,var(--review-approved) 45%,transparent)}
  #dash-docs-tab .dt-emb > span.tgt-meta{border:none;padding:0;margin-left:4px;font-weight:400}
  #dash-docs-tab .dt-acts{display:flex;gap:6px;flex-wrap:wrap;align-items:center}
  #dash-docs-tab .aidoc-actbtn[disabled]{opacity:.55;cursor:default}
  #dash-docs-tab .aidoc-actbtn.dt-configure{color:var(--accent)}
  #dash-docs-tab .dt-rowacts{display:flex;gap:5px;flex-wrap:wrap}

  /* ---- the AI generation tab (V0.44.1): the console's body inside the dashboard ---- */
  /* Lead sentence left, the console's three header buttons right, on one line. */
  #dash-ai-tab .dt-tab-lead{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:12px}
  #dash-ai-tab .dt-tab-lead .perm-section-lead{margin-bottom:0}
  #dash-ai-tab .dt-tab-actions{display:flex;gap:8px;flex:0 0 auto}
  /* The pipeline-stage strips inside a tab that is itself a tab: the summary strip's chip look,
     not the dashboard's filled tab look, so the two levels read as two levels. The ids/classes
     stay `#aidoc-tabs .perm-tab` — the suite reads them. */
  #dash-ai-tab .perm-tabs{flex-wrap:wrap;gap:8px;margin-bottom:14px}
  #dash-ai-tab .perm-tab{font-size:12.5px;font-weight:500;padding:5px 11px 5px 8px;border-radius:999px;gap:7px}
  #dash-ai-tab .perm-tab.on{background:color-mix(in srgb,var(--accent) 12%,transparent);border-color:var(--accent);color:var(--ink)}
  #dash-ai-tab .perm-tab.on .ico{color:var(--ink)}
  #dash-ai-tab .dt-aidoc-cell{display:flex;flex-direction:column;align-items:flex-start;gap:5px;min-width:0}
  #dash-ai-tab .dt-aidoc-cell .docnav-pill{max-width:100%}
  #dash-ai-tab .aidoc-wf{font-size:11.5px}
  #dash-ai-tab .rowstate-cell{vertical-align:middle}

  /* ---- the locale popover (portalled by MUI, so NOT under #dash-docs-tab) ---- */
  .dt-pop{width:340px;max-width:92vw;padding:12px 14px 14px;overflow:visible}
  /* The picker's own option menu is absolutely positioned inside the paper; the min-height is
     what keeps it from making the whole popover scroll — the rule `.wf-resp-panel` states next
     door, for the same picker and the same reason. `:has()` because the picker is drawn only
     for someone who may assign: without it the panel would be tall and empty for everyone
     else, and a browser lacking `:has()` merely scrolls, as this surface did before V0.42. */
  .dt-pop:has(.subj-picker){min-height:430px}
  .dt-pop .subj-list{max-height:170px}
  .dt-pop-row{display:flex;justify-content:space-between;gap:10px;font-size:12.5px;padding:5px 0;
    border-bottom:1px solid var(--line)}
  .dt-pop-row:last-of-type{border-bottom:none}
  .dt-pop-row .k{color:var(--ink-faint);flex:0 0 auto}
  .dt-pop-row .v{text-align:right;color:var(--ink);min-width:0}
  .dt-pop-sec{font-size:11px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;
    color:var(--ink-faint);margin:12px 0 6px}
  .dt-pop-note{font-size:11.5px;color:var(--ink-faint);margin-top:8px;line-height:1.45}
  .dt-pop-acts{display:flex;flex-wrap:wrap;gap:6px;margin-top:12px}

  /* ================================================================================== */
  /* ---- dashboard: the Projects tab (V0.50.2) --------------------------------------- */
  /* Composed from the vocabulary the region already has — .card, .utable, .perm-tabs.sub,
     .dt-strip-chip, the Pie, the NodeChip, .subj-av, the popup's .todorow — and scoped under
     the tab so no shared selector moves and neither :root block is touched. The stacked bar and
     the two schedule meters are the one new drawing; every colour in them is a token. */
  #dash-projects-tab .pj-lead{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:12px}
  #dash-projects-tab .pj-lead .perm-section-lead{margin-bottom:0;max-width:72ch}
  #dash-projects-tab .pj-lead-actions{display:flex;align-items:center;gap:10px;flex:0 0 auto}
  /* The Documents tab's icon button, re-scoped — that tab scopes its own the same way. */
  #dash-projects-tab .iconbtn{border:1px solid var(--line);background:var(--panel);color:var(--ink-dim);
    border-radius:8px;width:28px;height:28px;padding:0;display:inline-grid;place-items:center;cursor:pointer}
  #dash-projects-tab .iconbtn:hover{color:var(--ink);border-color:var(--line-strong)}
  #dash-projects-tab .iconbtn:focus-visible{outline:2px solid var(--accent);outline-offset:1px}
  #dash-projects-tab .iconbtn .ico{width:14px;height:14px}
  #dash-projects-tab .pj-stack{display:grid;gap:14px}
  #dash-projects-tab .card{margin-bottom:0}
  #dash-projects-tab .pj-card-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:10px}
  #dash-projects-tab .pj-card-head h3{margin:0}
  #dash-projects-tab .pj-card-head .sp{flex:1}
  #dash-projects-tab .pj-empty{color:var(--ink-faint);font-size:13px;padding:8px 0}
  #dash-projects-tab .pj-muted{color:var(--ink-faint)}
  #dash-projects-tab .tone-bad{color:var(--status-doc-rejected,#ef4444)}
  #dash-projects-tab .tone-warn{color:var(--review-in-review)}
  #dash-projects-tab .scroll{overflow-x:auto}

  /* ---- the strip: one chip per project ---- */
  #pj-strip{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:14px}
  .pj-chip{border:1px solid var(--line);background:var(--panel);color:var(--ink-dim);font:inherit;
    border-radius:12px;padding:8px 12px 8px 10px;display:inline-flex;align-items:center;gap:10px;
    cursor:pointer;text-align:left}
  .pj-chip:hover{border-color:var(--line-strong);color:var(--ink)}
  .pj-chip.on{border-color:var(--accent);box-shadow:0 0 0 2px color-mix(in srgb,var(--accent) 25%,transparent)}
  .pj-chip:focus-visible{outline:2px solid var(--accent);outline-offset:1px}
  .pj-chip-meta{display:flex;flex-direction:column;gap:5px;align-items:flex-start}
  .pj-chip-sub{display:flex;align-items:center;gap:8px;font-size:12px;flex-wrap:wrap}
  .pj-chip-frac{color:var(--ink);font-variant-numeric:tabular-nums}
  .pj-chip-due{font-size:11.5px;color:var(--ink-faint)}

  /* ---- the selected project's header ---- */
  .pj-hdr{display:flex;align-items:center;gap:18px;flex-wrap:wrap}
  .pj-hdr-main{flex:1;min-width:240px;display:flex;flex-direction:column;gap:10px}
  .pj-head-title{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
  .pj-crumb{font-size:12px;color:var(--ink-faint)}
  .pj-counts{display:flex;gap:8px;flex-wrap:wrap}
  #dash-projects-tab .dt-strip-chip.pj-static{cursor:default}
  /* The dot has no global base rule (it is scoped per tab); this tab gets its own, as Logs did. */
  #dash-projects-tab .dt-dot{width:9px;height:9px;border-radius:50%;flex:0 0 auto;background:var(--c,var(--ink-faint))}
  #dash-projects-tab .dt-dot.tone-good{--c:var(--review-approved)}
  #dash-projects-tab .dt-dot.tone-warn{--c:var(--review-in-review)}
  #dash-projects-tab .dt-dot.tone-bad{--c:var(--status-doc-rejected,#ef4444)}
  /* The action log's outcome pill, wearing the project's status. */
  .pj-status{display:inline-flex;align-items:center;gap:6px;padding:2px 9px;border-radius:999px;
    font-size:11.5px;font-weight:600;white-space:nowrap;border:1px solid var(--c);color:var(--c);
    background:color-mix(in srgb,var(--c) 12%,transparent)}
  .pj-status::before{content:'';width:7px;height:7px;border-radius:50%;background:var(--c);flex:0 0 auto}
  .pj-status-done{--c:var(--review-approved)}
  .pj-status-on-track{--c:var(--accent)}
  .pj-status-at-risk{--c:var(--review-in-review)}
  .pj-status-late{--c:var(--status-doc-rejected,#ef4444)}
  .pj-status-not-started{--c:var(--ink-faint)}

  /* ---- the schedule: time elapsed against work done ---- */
  .pj-schedule{margin-top:14px;display:grid;gap:8px}
  .pj-meter{display:grid;grid-template-columns:110px 1fr 48px;gap:10px;align-items:center;
    font-size:12.5px;color:var(--ink-dim)}
  .pj-meter-track{position:relative;height:8px;border-radius:4px;background:var(--panel-2)}
  .pj-meter-fill{position:absolute;top:0;bottom:0;left:0;border-radius:4px}
  .pj-meter-fill.elapsed{background:var(--line-strong)}
  .pj-meter-fill.done{background:var(--review-approved)}
  .pj-meter-today{position:absolute;top:-4px;bottom:-4px;width:2px;background:var(--ink);border-radius:1px;
    transform:translateX(-1px)}
  .pj-meter-pct{color:var(--ink);font-variant-numeric:tabular-nums;text-align:right}
  .pj-sched-ends{display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;font-size:12px;color:var(--ink-dim)}
  .pj-sched-ends b{color:var(--ink)}
  .pj-daysleft.tone-bad{font-weight:600}
  #dash-projects-tab .pj-noschedule{padding:0}

  /* ---- the bar every row wears ---- */
  .pj-bar{display:flex;gap:2px;height:8px;border-radius:4px;overflow:hidden;background:var(--panel-2);min-width:120px}
  .pj-bar-seg{height:100%;border-radius:2px}
  .pj-bar-seg.done{background:var(--review-approved)}
  .pj-bar-seg.overdue{background:var(--status-doc-rejected,#ef4444)}
  .pj-bar-seg.open{background:var(--line-strong)}
  .pj-frac{font-variant-numeric:tabular-nums;color:var(--ink-dim);white-space:nowrap}
  .pj-due{white-space:nowrap}
  .pj-due.tone-bad{font-weight:600}

  /* ---- the two tables ---- */
  #dash-projects-tab tr.pj-expandable,#dash-projects-tab tr.pj-assignee{cursor:pointer}
  #dash-projects-tab tr.pj-expandable:hover td,#dash-projects-tab tr.pj-assignee:hover td{
    background:color-mix(in srgb,var(--accent) 6%,transparent)}
  #dash-projects-tab tr.open td{background:color-mix(in srgb,var(--accent) 9%,transparent)}
  .pj-nodecell{display:flex;align-items:center;gap:8px;white-space:nowrap}
  #dash-projects-tab .pj-node-caret{width:11px;height:11px;flex:0 0 auto;fill:none;stroke:currentColor;
    stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round;color:var(--ink-faint);
    transform:rotate(-90deg);transition:transform .15s ease}
  #dash-projects-tab .pj-node-caret.open{transform:rotate(0deg)}
  #dash-projects-tab .pj-node-caret.blank{visibility:hidden}
  .pj-badge{font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--accent);
    border:1px solid color-mix(in srgb,var(--accent) 45%,transparent);border-radius:999px;padding:1px 7px}
  .pj-here{font-size:11.5px;color:var(--ink-faint)}
  .pj-avs{display:inline-flex;align-items:center}
  .pj-avs .subj-av{margin-left:-6px;box-shadow:0 0 0 2px var(--panel)}
  .pj-avs .subj-av:first-child{margin-left:0}
  .pj-more{font-size:11px;color:var(--ink-faint);margin-left:6px}
  .subj-av.pj-av-none{background:transparent;border:1px dashed var(--line-strong);color:var(--ink-faint)}
  #dash-projects-tab tr.pj-detail td{background:var(--panel-2);padding:6px 10px 10px 34px}
  #dash-projects-tab tr.pj-detail:hover td{background:var(--panel-2)}
  #dash-projects-tab .todorow{padding:4px 7px;border-top:2px solid transparent}
  #dash-projects-tab .todorow:hover{background:var(--panel)}
  .pj-todo-node{font-size:11.5px;color:var(--ink-faint);white-space:nowrap;flex:0 0 auto}
  #dash-projects-tab .todo-notes{background:var(--panel)}
  #dash-projects-tab .todo-empty{padding:2px 0}

  /* ---- the burn-up ---- */
  .pj-burnup{display:block;width:100%;height:auto;max-height:300px}
  .pj-legend{display:flex;gap:14px;font-size:12px;color:var(--ink-dim);flex-wrap:wrap;font-family:var(--font);font-weight:400}
  .pj-legend i{display:inline-block;width:14px;height:3px;border-radius:2px;vertical-align:middle;margin-right:6px}
  #dash-projects-tab .pj-hint{margin-top:8px;display:block}

  /* ---- the project mark in the popup's HEAD (V0.50.3) ---------------------------------- */
  /* Fourth control after the seat, the star, the bell and the address button. The head's own
     vocabulary: a `.favbtn` lit in the address button's accent when the node is a project, the
     To-dos editor's compact date fields to its right, a fourth `.favbtn` for Progress. Nothing
     draws for a non-project unless the reader may mark one, so node-popup.png is unchanged. */
  /* THE TOOLS CLUSTER RIGHT (V0.51): one group at the far end, so the seat has the line and
     the head stops reading as a row of controls with a node at the front of it. */
  #popup h3 .pp-headtools{margin-left:auto;display:inline-flex;align-items:center;gap:2px;flex:0 0 auto;
    flex-wrap:wrap;justify-content:flex-end}
  #popup h3 .pp-project{display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap;min-width:0}
  /* The dates at rest (V0.51) — the reading shape's own range, as a button. Two date inputs
     open on the seat's line made the head a form; this says the same thing in a third of the
     width until somebody wants to change it. */
  #popup h3 .pp-datepill{display:inline-flex;align-items:center;gap:5px;border:none;background:none;
    padding:3px 8px;border-radius:999px;cursor:pointer;font-family:var(--font);font-size:12.5px;
    font-weight:500;letter-spacing:0;color:var(--ink-dim);white-space:nowrap}
  #popup h3 .pp-datepill:hover{background:var(--panel-2);color:var(--ink)}
  #popup h3 .pp-datepill:focus-visible{outline:2px solid var(--accent);outline-offset:1px}
  #popup h3 .pp-datepill:disabled{opacity:.5;cursor:default}
  #popup .favbtn.projbtn.on,#popup .favbtn.projbtn.on:hover{color:var(--accent);
    background:color-mix(in srgb,var(--accent) 14%,transparent)}
  #popup .favbtn.projbtn:disabled{opacity:.5;cursor:default}
  #popup h3 .pp-project-pill{display:inline-flex;align-items:center;gap:5px;border-radius:999px;padding:3px 10px 3px 8px;
    font-family:var(--font);font-size:12px;font-weight:600;color:var(--accent);letter-spacing:0;
    border:1px solid color-mix(in srgb,var(--accent) 45%,transparent);background:color-mix(in srgb,var(--accent) 12%,transparent)}
  #popup h3 .pp-project-pill .ico{width:13px;height:13px}
  #popup h3 .pp-head-dates{display:inline-flex;align-items:center;gap:6px;font-family:var(--font);font-size:12.5px;
    font-weight:500;color:var(--ink-dim);letter-spacing:0}
  #popup h3 .pp-head-dates.read{color:var(--ink)}
  #popup h3 .pp-head-arrow{color:var(--ink-faint)}
  /* `.todoedit-due` already defeats `#popup label`'s heading look; inside the h3 the field is
     a size smaller than in the editor, so it sits on the row rather than making it a form. */
  #popup h3 label.pp-head-date{font-size:12px;gap:5px}
  /* The native `input[type=date]` rules that stood here went with V0.51.1: the field is a
     `.due-btn` now, styled once below with every other date field in the app. */

  /* ---- the project mark on the MAP (V0.50.3) ------------------------------------------ */
  /* Inside the box, between the swatch and the label: every corner is already spoken for (the
     clearance badge, presence, the meta dots, the doc pills). The same flag as the head and the
     dashboard tab, in the accent. Sized to the 13.5px label so a project node keeps its height. */
  .node .pj-mark{width:14px;height:14px;flex:0 0 auto;color:var(--accent);
    fill:none;stroke:currentColor;stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round;margin:0 -2px}
  .node.root .pj-mark{color:#fff}
  /* HEATMAP MODE ONLY: the subtree's progress along the inside of the bottom edge — the mode
     that already paints every node by its state. `.pj-bar` buys the 3px the bar needs; the
     layout pass MEASURES node heights, so the room is real and the wires follow. */
  .node.pj-bar{padding-bottom:13px}
  .node .pj-under{position:absolute;left:11px;right:11px;bottom:5px;height:3px;border-radius:2px;
    background:color-mix(in srgb,var(--accent) 28%,transparent);overflow:hidden;pointer-events:none}
  .node .pj-under i{display:block;height:100%;background:var(--review-approved);border-radius:2px}
